import FilePreviewModal from "../components/FilePreviewModal";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import AIInsightsPanel from "../components/AIInsightsPanel";
import {
  FaUserPlus,
  FaSearch,
  FaSync,
  FaUsers,
  FaUserTie,
  FaUmbrellaBeach,
  FaUserClock,
  FaEdit,
  FaTrash,
  FaEnvelope,
  FaPhone,
  FaBuilding,
  FaIdBadge,
  FaFileExcel,
  FaFilePdf,
  FaClipboardList,
  FaBirthdayCake,
  FaBan,
  FaCalendarCheck,
  FaDownload,
  FaExternalLinkAlt,
  FaFileImport,
  FaUpload,
  FaBriefcase,
  FaFileWord,
  FaPlus,
  FaMoneyBillWave,
  FaRobot,
  FaGift,
} from "react-icons/fa";
import { read as readXLSX, utils as XLSXUtils, writeFile as writeXLSXFile } from "@redoper1/xlsx-js-style";
import Modal from "../components/Modal";
import HumanResourcesService from "../services/HumanResourcesService";
import { formatDateOnly, normalizeDateInput } from "../utils/dateOnly";
import { useAuth } from "../AuthContext";
import { hasPermission, HUMAN_RESOURCES_MANAGE_PERMISSION } from "../utils/permissions";

const STATUS_OPTIONS = [
  { value: "CURRENT", label: "Ativos, férias e licenças" },
  { value: "ALL", label: "Todos os status" },
  { value: "ACTIVE", label: "Ativo" },
  { value: "ON_LEAVE", label: "Licença" },
  { value: "VACATION", label: "Férias" },
  { value: "INACTIVE", label: "Inativo" },
  { value: "TERMINATED", label: "Desligado" },
];

const STATUS_LABELS = {
  ACTIVE: "Ativo",
  ON_LEAVE: "Licença",
  VACATION: "Férias",
  INACTIVE: "Inativo",
  TERMINATED: "Desligado",
};

const STATUS_STYLES = {
  ACTIVE: "bg-emerald-100 text-emerald-700",
  ON_LEAVE: "bg-amber-100 text-amber-700",
  VACATION: "bg-sky-100 text-sky-700",
  INACTIVE: "bg-slate-100 text-slate-600",
  TERMINATED: "bg-rose-100 text-rose-700",
};

const TAB_ITEMS = [
  { id: "overview", label: "Colaboradores", icon: FaUsers },
  { id: "ai", label: "Insights IA", icon: FaRobot },
  { id: "recruitmentSelection", label: "Recrutamento e Seleção", icon: FaBriefcase },
];

const OVERVIEW_QUICK_TABS = [
  { id: "summary", label: "Painel", icon: FaUsers },
  { id: "terminatedEmployees", label: "Desligados", icon: FaBan },
  { id: "birthdays", label: "Aniversariantes", icon: FaBirthdayCake },
  { id: "withoutAccess", label: "Sem acesso", icon: FaIdBadge },
  { id: "employeeHistory", label: "Histórico", icon: FaClipboardList },
  { id: "benefits", label: "Benefícios", icon: FaGift },
  { id: "vacationControl", label: "Controle de férias", icon: FaUmbrellaBeach },
];

const HISTORY_TYPE_OPTIONS = [
  { value: "LEAVE", label: "Licença" },
  { value: "VACATION", label: "Férias" },
  { value: "OCCURRENCE", label: "Ocorrências" },
  { value: "TERMINATION", label: "Desligamento" },
];

const HISTORY_TYPE_LABELS = {
  LEAVE: "Licença",
  VACATION: "Férias",
  OCCURRENCE: "Ocorrência",
  TERMINATION: "Desligamento",
};

const EMPLOYEE_IMPORT_COLUMNS = [
  { key: "registrationNumber", label: "Matrícula", required: false, instructions: "Código interno de identificação." },
  { key: "fullName", label: "Nome completo", required: true, instructions: "Nome do colaborador." },
  { key: "email", label: "E-mail", required: false, instructions: "E-mail corporativo." },
  { key: "phone", label: "Telefone", required: false, instructions: "Telefone principal." },
  { key: "department", label: "Departamento", required: false, instructions: "Ex: Financeiro, RH, Operações." },
  { key: "position", label: "Cargo", required: false, instructions: "Cargo atual do colaborador." },
  { key: "employmentType", label: "Tipo de vínculo", required: false, instructions: "CLT, PJ, Estágio, Temporário, Terceirizado, Aprendiz ou Autônomo." },
  { key: "hireDate", label: "Data de admissão", required: false, instructions: "Formato AAAA-MM-DD." },
  { key: "salary", label: "Salário", required: false, instructions: "Valor numérico. Exemplo: 3500.50." },
  { key: "vacationDays", label: "Saldo de férias", required: false, instructions: "Quantidade de dias disponível." },
  { key: "status", label: "Status", required: true, instructions: "Ativo, Licença, Férias, Inativo ou Desligado." },
  { key: "notes", label: "Observações", required: false, instructions: "Informações adicionais." },
];

const HISTORY_IMPORT_COLUMNS = [
  { key: "registrationNumber", label: "Matrícula do colaborador", required: false, instructions: "Preferencial para localizar o colaborador." },
  { key: "employeeEmail", label: "E-mail do colaborador", required: false, instructions: "Alternativa para localizar o colaborador." },
  { key: "employeeName", label: "Nome do colaborador", required: false, instructions: "Alternativa quando matrícula/e-mail não forem informados." },
  { key: "type", label: "Tipo de histórico", required: true, instructions: "Licença, Férias, Ocorrências ou Desligamento." },
  { key: "startDate", label: "Data de início", required: false, instructions: "Formato AAAA-MM-DD." },
  { key: "endDate", label: "Data de fim", required: false, instructions: "Formato AAAA-MM-DD." },
  { key: "days", label: "Dias", required: false, instructions: "Obrigatório para tipo Férias." },
  { key: "notes", label: "Observações", required: false, instructions: "Detalhes complementares." },
];

const VACATION_IMPORT_COLUMNS = [
  { key: "cpf", label: "CPF", required: true, instructions: "CPF do colaborador, com ou sem pontuação." },
  { key: "startDate", label: "Data de início", required: true, instructions: "Data inicial das férias. O sistema calcula automaticamente a data final." },
  { key: "days", label: "Quantidade de dias", required: true, instructions: "Quantidade total de dias de férias." },
];

const getImportColumnLabel = (column) => `${column.required ? "* " : ""}${column.label}`;
const normalizeImportHeader = (header) =>
  String(header || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\*/g, "")
    .toLowerCase()
    .trim();
const normalizeImportText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
const normalizeImportCpf = (value) => String(value || "").replace(/\D/g, "");


const coerceImportValue = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value).trim();
};

const EMPLOYEE_DOCUMENT_CATEGORIES = [
  "Contrato de trabalho",
  "Termos e declarações",
  "Documentos pessoais",
  "Comprovantes",
  "Atestados",
  "Advertências",
  "Certificados",
  "Exames admissionais e periódicos",
  "ASO",
  "Fichas de entrega de EPI",
  "Avaliações",
  "Documentos de desligamento.",
];

const EMPLOYMENT_TYPES = [
  "CLT",
  "PJ",
  "Estágio",
  "Temporário",
  "Terceirizado",
  "Aprendiz",
  "Autônomo",
];


const BENEFIT_CATALOG_STORAGE_KEY = "humanResourcesBenefitCatalog";
const BENEFIT_EXCLUSIONS_STORAGE_KEY = "humanResourcesBenefitExclusions";
const defaultBenefitForm = { name: "", monthlyCost: "", employmentTypes: [] };

const normalizeBenefitCatalog = (items) => {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => ({
      id: item?.id || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name: String(item?.name || "").trim(),
      monthlyCost: item?.monthlyCost ? maskCurrency(String(item.monthlyCost)) : "",
      employmentTypes: Array.isArray(item?.employmentTypes)
        ? item.employmentTypes.filter((type) => EMPLOYMENT_TYPES.includes(type))
        : [],
    }))
    .filter((item) => item.name);
};

const loadStoredBenefitCatalog = () => {
  if (typeof window === "undefined") return [];
  try {
    return normalizeBenefitCatalog(JSON.parse(window.localStorage.getItem(BENEFIT_CATALOG_STORAGE_KEY) || "[]"));
  } catch (error) {
    return [];
  }
};

const loadStoredBenefitExclusions = () => {
  if (typeof window === "undefined") return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(BENEFIT_EXCLUSIONS_STORAGE_KEY) || "{}");
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).map(([employeeId, ids]) => [employeeId, Array.isArray(ids) ? ids.map(String) : []])
    );
  } catch (error) {
    return {};
  }
};

const getBenefitSignature = (benefit) => `${String(benefit?.name || "").trim().toLowerCase()}|${maskCurrency(String(benefit?.monthlyCost || ""))}`;

const mergeCatalogBenefitsIntoRows = (rows, catalog, employmentType, excludedIds = []) => {
  const existingRows = Array.isArray(rows) && rows.length ? rows : [];
  const excluded = new Set((excludedIds || []).map(String));
  const existingSignatures = new Set(existingRows.map(getBenefitSignature).filter((signature) => signature !== "|"));
  const normalizedExisting = existingRows.length ? existingRows : [{ name: "", monthlyCost: "" }];
  const automaticRows = catalog
    .filter((benefit) => benefit.employmentTypes.includes(employmentType))
    .filter((benefit) => !excluded.has(String(benefit.id)) && !excluded.has(getBenefitSignature(benefit)))
    .filter((benefit) => !existingSignatures.has(getBenefitSignature(benefit)))
    .map((benefit) => ({
      name: benefit.name,
      monthlyCost: benefit.monthlyCost,
    }));
  return [...normalizedExisting, ...automaticRows];
};

const sumBenefitRowsMonthlyCost = (benefits = []) =>
  (Array.isArray(benefits) ? benefits : []).reduce(
    (total, benefit) => total + parseCurrencyInput(benefit?.monthlyCost),
    0
  );

const EMPLOYEE_REPORT_FIELDS = [
  { key: "fullName", label: "Nome completo" },
  { key: "email", label: "E-mail" },
  { key: "phone", label: "Telefone" },
  { key: "department", label: "Departamento / Cargo" },
  { key: "employmentType", label: "Tipo de vínculo" },
  { key: "status", label: "Status" },
  { key: "hireDate", label: "Data de admissão" },
  { key: "terminationDate", label: "Data de desligamento" },
  { key: "systemAccess", label: "Acesso ao sistema" },
  { key: "vacationDelegates", label: "Substitutos em férias" },
  { key: "salary", label: "Salário" },
  { key: "vacationDays", label: "Saldo de férias" },
];

const DEFAULT_PDF_FIELD_KEYS = ["fullName", "department", "status", "hireDate", "systemAccess", "vacationDelegates"];

const defaultFilters = {
  search: "",
  status: "CURRENT",
  department: "",
  employmentType: "ALL",
};

const FORM_SECTION_CLASS = "rounded-2xl border border-slate-200 bg-slate-50/70 p-4 shadow-sm";
const FORM_SECTION_HEADER_CLASS = "mb-4 flex items-center gap-3 border-b border-slate-200 pb-3";
const FORM_SECTION_BADGE_CLASS = "flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-sm font-bold text-emerald-700";
const FORM_SECTION_TITLE_CLASS = "text-sm font-semibold uppercase tracking-wide text-slate-700";


const HumanResourcesLoadingBar = ({ progress, label }) => (
  <div
    className="overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-sm"
    role="status"
    aria-live="polite"
    aria-label={label}
  >
    <div className="flex items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-wide text-blue-950">
      <span>{label}</span>
      <span>{progress}%</span>
    </div>
    <div className="h-2 w-full bg-slate-100">
      <div
        className="h-full rounded-r-full bg-gradient-to-r from-blue-950 via-sky-500 to-emerald-400 transition-all duration-500 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  </div>
);

const defaultFormState = {
  registrationNumber: "",
  fullName: "",
  photoUrl: "",
  socialName: "",
  email: "",
  phone: "",
  cpf: "",
  rg: "",
  birthDate: "",
  gender: "",
  maritalStatus: "",
  department: "",
  position: "",
  employmentType: "",
  hireDate: "",
  terminationDate: "",
  workShift: "",
  salary: "",
  netSalary: "",
  benefits: [{ name: "", monthlyCost: "" }],
  fgtsPercentage: "8",
  employerInssExempt: "false",
  vacationDays: "",
  notes: "",
  addressStreet: "",
  addressNumber: "",
  addressComplement: "",
  addressNeighborhood: "",
  addressCity: "",
  addressState: "",
  addressZipCode: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
  status: "ACTIVE",
  userId: "",
  vacationDelegateUserIds: [],
  employeeFiles: [{ name: "", category: EMPLOYEE_DOCUMENT_CATEGORIES[0], dataUrl: "" }],
};

const defaultTalentForm = {
  candidateName: "",
  email: "",
  phone: "",
  position: "",
  notes: "",
};

const TALENT_JOB_STATUS_OPTIONS = ["Rascunho", "Em andamento", "Concluído"];

const defaultTalentJobForm = {
  name: "",
  status: "Rascunho",
  publicationDate: new Date().toISOString().slice(0, 10),
  description: "",
  requirements: "",
  salary: "",
  benefits: [""],
  workload: "",
  notes: "",
};

const TALENT_ATTACHMENT_ACCEPT = [
  ".pdf",
  ".doc",
  ".docx",
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
  ".gif",
  ".bmp",
  ".tif",
  ".tiff",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "image/bmp",
  "image/tiff",
].join(",");

const readTalentFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error || new Error("Não foi possível ler o arquivo."));
  reader.readAsDataURL(file);
});

const defaultTalentFilters = {
  name: "",
  email: "",
  position: "",
};

const defaultHistoryForm = {
  id: null,
  employeeId: "",
  type: "VACATION",
  startDate: "",
  endDate: "",
  days: "",
  notes: "",
};

const defaultOverview = {
  summary: {
    total: 0,
    active: 0,
    vacation: 0,
    onLeave: 0,
    inactive: 0,
    terminated: 0,
    withSystemAccess: 0,
    withoutSystemAccess: 0,
    hrMonthlyCost: 0,
    benefitsMonthlyCost: 0,
  },
  headcountByDepartment: [],
  employmentTypeDistribution: [],
  recentHires: [],
  upcomingBirthdays: [],
  vacationSchedule: [],
  withoutSystemAccess: [],
};

const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

const maskCpf = (value) => {
  const digits = digitsOnly(value).slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
};

const maskPhone = (value) => {
  const digits = digitsOnly(value).slice(0, 11);
  if (digits.length <= 10) {
    return digits.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
  }
  return digits.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
};

const maskCep = (value) => digitsOnly(value).slice(0, 8).replace(/(\d{5})(\d)/, "$1-$2");

const maskCurrency = (value) => {
  const digits = digitsOnly(value);
  if (!digits) return "";
  const numeric = Number(digits) / 100;
  return numeric.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const formatCurrencyInputValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const numeric = Number(String(value).replace(",", "."));
  if (Number.isFinite(numeric)) {
    return numeric.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  }
  return maskCurrency(value);
};

const createOverviewFallback = () => ({
  summary: { ...defaultOverview.summary },
  headcountByDepartment: [],
  employmentTypeDistribution: [],
  recentHires: [],
  upcomingBirthdays: [],
  vacationSchedule: [],
  withoutSystemAccess: [],
});

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "—";
  }
  return numeric.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const parseCurrencyInput = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const normalized = String(value)
    .replace(/[^\d,.-]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const CLT_COST_EMPLOYMENT_TYPES = new Set(["clt", "aprendiz"]);

const normalizeEmploymentType = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .trim();

const isCltCostEmploymentType = (value) => CLT_COST_EMPLOYMENT_TYPES.has(normalizeEmploymentType(value));

const parsePercentageInput = (value) => {
  const parsed = Number(String(value || "0").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
};

const calculateFinancialCost = (form, taxSettings = {}) => {
  const salary = parseCurrencyInput(form.salary);
  const benefitsTotal = (form.benefits || []).reduce(
    (sum, benefit) => sum + parseCurrencyInput(benefit.monthlyCost),
    0
  );

  if (!isCltCostEmploymentType(form.employmentType)) {
    return {
      salary,
      benefitsTotal,
      fgtsCost: 0,
      vacationProvision: 0,
      vacationThirdProvision: 0,
      thirteenthProvision: 0,
      employerInssCost: 0,
      pisCost: 0,
      total: salary + benefitsTotal,
    };
  }

  const vacationProvision = salary / 12;
  const vacationThirdProvision = vacationProvision / 3;
  const thirteenthProvision = salary / 12;
  const payrollTaxBase = salary + vacationProvision + vacationThirdProvision + thirteenthProvision;
  const fgtsCost = payrollTaxBase * (parsePercentageInput(form.fgtsPercentage) / 100);
  const employerInssCost = taxSettings.employerInssExempt
    ? 0
    : salary * (parsePercentageInput(taxSettings.employerInssPercentage) / 100);
  const pisCost = taxSettings.pisExempt ? 0 : payrollTaxBase * 0.01;

  return {
    salary,
    benefitsTotal,
    fgtsCost,
    vacationProvision,
    vacationThirdProvision,
    thirteenthProvision,
    employerInssCost,
    pisCost,
    total: salary + benefitsTotal + fgtsCost + vacationProvision + vacationThirdProvision + thirteenthProvision + employerInssCost + pisCost,
  };
};

const formatDepartment = (department, position) => {
  if (!department && !position) {
    return "—";
  }
  if (!department) {
    return position;
  }
  if (!position) {
    return department;
  }
  return `${department} • ${position}`;
};

const formatEmploymentType = (value) => {
  if (!value) {
    return "—";
  }
  return value;
};


const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() || "").join("");
};

const formatDateRange = (start, end) => {
  if (!start && !end) {
    return "—";
  }
  if (start && end) {
    return `${formatDateOnly(start)} até ${formatDateOnly(end)}`;
  }
  if (start) {
    return `A partir de ${formatDateOnly(start)}`;
  }
  return `Até ${formatDateOnly(end)}`;
};

const formatDaysLabel = (value) => {
  if (value === null || value === undefined) {
    return "—";
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "—";
  }
  if (numeric === 0) return "0 dias";
  if (numeric === 1) return "1 dia";
  return `${numeric} dias`;
};


const calculateAcquisitionEndDate = (startDate) => {
  if (!startDate) {
    return "";
  }
  const date = new Date(`${startDate}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  date.setUTCFullYear(date.getUTCFullYear() + 1);
  return date.toISOString().slice(0, 10);
};

const calculateVacationEndDate = (startDate, days) => {
  if (!startDate || !days) {
    return "";
  }
  const totalDays = Number(days);
  if (!Number.isFinite(totalDays) || totalDays <= 0) {
    return "";
  }
  const date = new Date(`${startDate}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + totalDays - 1);
  return date.toISOString().slice(0, 10);
};

export default function HumanResources() {
  const { user } = useAuth();
  const canManage = useMemo(
    () => hasPermission(user, HUMAN_RESOURCES_MANAGE_PERMISSION),
    [user]
  );

  const [filters, setFilters] = useState({ ...defaultFilters });
  const [filterForm, setFilterForm] = useState({ ...defaultFilters });
  const [employees, setEmployees] = useState([]);
  const [terminatedEmployees, setTerminatedEmployees] = useState([]);
  const [terminatedLoading, setTerminatedLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [previewEmployeeFile, setPreviewEmployeeFile] = useState(null);
  const loadEmployeePreview = useCallback(({ signal } = {}) => HumanResourcesService.fetchEmployeeFile(previewEmployeeFile, { signal }), [previewEmployeeFile]);
  const [form, setForm] = useState({ ...defaultFormState });
  const [benefitCatalog, setBenefitCatalog] = useState(loadStoredBenefitCatalog);
  const [benefitExclusions, setBenefitExclusions] = useState(loadStoredBenefitExclusions);
  const [benefitForm, setBenefitForm] = useState({ ...defaultBenefitForm });
  const [benefitEditingId, setBenefitEditingId] = useState(null);
  const [benefitError, setBenefitError] = useState("");
  const [companyTaxSettings, setCompanyTaxSettings] = useState({ employerInssExempt: false, employerInssPercentage: "20", pisExempt: false });
  const financialCost = useMemo(() => calculateFinancialCost(form, companyTaxSettings), [form, companyTaxSettings]);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [overview, setOverview] = useState(defaultOverview);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState("");
  const [vacationControl, setVacationControl] = useState([]);
  const [vacationControlLoading, setVacationControlLoading] = useState(true);
  const [vacationControlError, setVacationControlError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [recruitmentTab, setRecruitmentTab] = useState("talentBank");
  const [overviewQuickTab, setOverviewQuickTab] = useState("summary");
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportError, setReportError] = useState("");
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [selectedPdfFields, setSelectedPdfFields] = useState(DEFAULT_PDF_FIELD_KEYS);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyForm, setHistoryForm] = useState({ ...defaultHistoryForm });
  const [historySaving, setHistorySaving] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [employeeHistory, setEmployeeHistory] = useState([]);
  const [focusedHistoryEmployeeId, setFocusedHistoryEmployeeId] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyEmployeeSearch, setHistoryEmployeeSearch] = useState("");
  const [activeEmployeeOptions, setActiveEmployeeOptions] = useState([]);
  const [vacationEditModalOpen, setVacationEditModalOpen] = useState(false);
  const [vacationEditSaving, setVacationEditSaving] = useState(false);
  const [vacationEditError, setVacationEditError] = useState("");
  const [vacationEditForm, setVacationEditForm] = useState({
    employeeId: "",
    vacationDays: "",
    acquisitionPeriodStart: "",
    acquisitionPeriodEnd: "",
  });
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importType, setImportType] = useState("EMPLOYEES");
  const [importingData, setImportingData] = useState(false);
  const [importError, setImportError] = useState("");
  const [importSummary, setImportSummary] = useState("");
  const [talentForm, setTalentForm] = useState({ ...defaultTalentForm });
  const [talentCurriculumFile, setTalentCurriculumFile] = useState(null);
  const [talentBankEntries, setTalentBankEntries] = useState([]);
  const [talentFilters, setTalentFilters] = useState({ ...defaultTalentFilters });
  const [talentError, setTalentError] = useState("");
  const [talentLoading, setTalentLoading] = useState(false);
  const [talentSaving, setTalentSaving] = useState(false);
  const [talentEditingId, setTalentEditingId] = useState(null);
  const [viewingTalentEntry, setViewingTalentEntry] = useState(null);
  const [talentJobs, setTalentJobs] = useState([]);
  const [talentJobsLoading, setTalentJobsLoading] = useState(false);
  const [talentJobSaving, setTalentJobSaving] = useState(false);
  const [talentJobError, setTalentJobError] = useState("");
  const [talentJobEditingId, setTalentJobEditingId] = useState(null);
  const [talentJobForm, setTalentJobForm] = useState({ ...defaultTalentJobForm });

  const requestFilters = useMemo(
    () => ({
      search: filters.search || undefined,
      status:
        filters.status && filters.status !== "ALL" ? filters.status : undefined,
      department: filters.department || undefined,
      employmentType:
        filters.employmentType && filters.employmentType !== "ALL"
          ? filters.employmentType
          : undefined,
    }),
    [filters]
  );

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(BENEFIT_CATALOG_STORAGE_KEY, JSON.stringify(benefitCatalog));
    }
  }, [benefitCatalog]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(BENEFIT_EXCLUSIONS_STORAGE_KEY, JSON.stringify(benefitExclusions));
    }
  }, [benefitExclusions]);

  const resetBenefitForm = () => {
    setBenefitForm({ ...defaultBenefitForm });
    setBenefitEditingId(null);
    setBenefitError("");
  };

  const toggleBenefitEmploymentType = (type) => {
    setBenefitForm((prev) => ({
      ...prev,
      employmentTypes: prev.employmentTypes.includes(type)
        ? prev.employmentTypes.filter((item) => item !== type)
        : [...prev.employmentTypes, type],
    }));
  };

  const handleBenefitCatalogSubmit = (event) => {
    event.preventDefault();
    const name = benefitForm.name.trim();
    if (!name) {
      setBenefitError("Informe o nome do benefício.");
      return;
    }
    if (!benefitForm.employmentTypes.length) {
      setBenefitError("Selecione ao menos um tipo de vínculo.");
      return;
    }

    const payload = {
      id: benefitEditingId || `${Date.now()}`,
      name,
      monthlyCost: maskCurrency(benefitForm.monthlyCost),
      employmentTypes: benefitForm.employmentTypes,
    };
    setBenefitCatalog((prev) =>
      benefitEditingId
        ? prev.map((item) => (item.id === benefitEditingId ? payload : item))
        : [...prev, payload]
    );
    resetBenefitForm();
  };

  const editBenefitCatalog = (benefit) => {
    setBenefitEditingId(benefit.id);
    setBenefitForm({ name: benefit.name, monthlyCost: benefit.monthlyCost, employmentTypes: benefit.employmentTypes || [] });
    setBenefitError("");
  };

  const removeBenefitCatalog = (benefitId) => {
    setBenefitCatalog((prev) => prev.filter((benefit) => benefit.id !== benefitId));
    if (benefitEditingId === benefitId) {
      resetBenefitForm();
    }
  };

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const list = await HumanResourcesService.listEmployees(requestFilters);
      setEmployees(list);
    } catch (err) {
      const message =
        err?.response?.data?.error || "Não foi possível carregar os colaboradores.";
      setEmployees([]);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [requestFilters]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const loadTerminatedEmployees = useCallback(async () => {
    setTerminatedLoading(true);
    try {
      const list = await HumanResourcesService.listEmployees({ status: "TERMINATED" });
      setTerminatedEmployees(list);
    } catch (err) {
      setTerminatedEmployees([]);
      setError(err?.response?.data?.error || "Não foi possível carregar colaboradores desligados.");
    } finally {
      setTerminatedLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTerminatedEmployees();
  }, [loadTerminatedEmployees]);

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError("");
    try {
      const data = await HumanResourcesService.getOverview();
      if (data && typeof data === "object") {
        setOverview(data);
      } else {
        setOverview(createOverviewFallback());
      }
    } catch (err) {
      const message =
        err?.response?.data?.error || "Não foi possível carregar os indicadores de RH.";
      setOverview(createOverviewFallback());
      setOverviewError(message);
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const loadVacationControl = useCallback(async () => {
    setVacationControlLoading(true);
    setVacationControlError("");
    try {
      const list = await HumanResourcesService.listVacationControl();
      setVacationControl(list);
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        "Não foi possível carregar o controle de férias.";
      setVacationControl([]);
      setVacationControlError(message);
    } finally {
      setVacationControlLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVacationControl();
  }, [loadVacationControl]);

  const loadEmployeeHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const list = await HumanResourcesService.listEmployeeHistory();
      setEmployeeHistory(list);
    } catch (err) {
      setEmployeeHistory([]);
      setError(err?.response?.data?.error || "Não foi possível carregar o histórico.");
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmployeeHistory();
  }, [loadEmployeeHistory]);

  const loadActiveEmployeesForHistory = useCallback(async () => {
    try {
      const activeList = await HumanResourcesService.listEmployees({ status: "ACTIVE" });
      setActiveEmployeeOptions(activeList);
    } catch (err) {
      setActiveEmployeeOptions([]);
    }
  }, []);

  const departmentSuggestions = useMemo(() => {
    const suggestionSet = new Set();
    employees.forEach((employee) => {
      if (employee.department) {
        suggestionSet.add(employee.department);
      }
    });
    return Array.from(suggestionSet).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [employees]);

  const delegateOptions = useMemo(() => {
    const currentUserId = form.userId ? String(form.userId) : "";
    return [...availableUsers]
      .filter((option) => String(option.id) !== currentUserId)
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }, [availableUsers, form.userId]);

  const currentEditingEmployee = useMemo(
    () => employees.find((employee) => employee.id === editingId) || null,
    [employees, editingId]
  );

  const focusedHistoryEmployee = useMemo(
    () => employees.find((employee) => employee.id === focusedHistoryEmployeeId) || null,
    [employees, focusedHistoryEmployeeId]
  );

  const visibleEmployeeHistory = useMemo(() => {
    if (!focusedHistoryEmployeeId) return employeeHistory;
    return employeeHistory.filter((entry) => Number(entry.employeeId) === Number(focusedHistoryEmployeeId));
  }, [employeeHistory, focusedHistoryEmployeeId]);


  const loadTalentBankEntries = useCallback(async () => {
    setTalentLoading(true);
    try {
      const list = await HumanResourcesService.listTalentBank();
      setTalentBankEntries(list);
    } catch (err) {
      setTalentBankEntries([]);
      setTalentError(err?.response?.data?.error || "Não foi possível carregar o Banco de Talentos.");
    } finally {
      setTalentLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTalentBankEntries();
  }, [loadTalentBankEntries]);

  const loadTalentJobs = useCallback(async () => {
    setTalentJobsLoading(true);
    try {
      const list = await HumanResourcesService.listTalentJobs();
      setTalentJobs(list);
    } catch (err) {
      setTalentJobs([]);
      setTalentJobError(err?.response?.data?.error || "Não foi possível carregar as vagas.");
    } finally {
      setTalentJobsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTalentJobs();
  }, [loadTalentJobs]);

  useEffect(() => {
    let mounted = true;
    HumanResourcesService.getCompanyProfile()
      .then((company) => {
        if (!mounted || !company) return;
        setCompanyTaxSettings({
          employerInssExempt: Boolean(company.employerInssExempt),
          employerInssPercentage: company.employerInssPercentage === null || company.employerInssPercentage === undefined ? "20" : String(company.employerInssPercentage),
          pisExempt: Boolean(company.pisExempt),
        });
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const talentJobOptions = useMemo(() => talentJobs.map((job) => job.name).filter(Boolean), [talentJobs]);

  const filteredTalentBankEntries = useMemo(() => {
    const normalize = (value) => String(value || "").toLowerCase().trim();
    const nameQuery = normalize(talentFilters.name);
    const emailQuery = normalize(talentFilters.email);
    const positionQuery = normalize(talentFilters.position);

    return talentBankEntries.filter((entry) => {
      const matchesName = !nameQuery || normalize(entry.candidateName).includes(nameQuery);
      const matchesEmail = !emailQuery || normalize(entry.email).includes(emailQuery);
      const matchesPosition = !positionQuery || normalize(entry.position).includes(positionQuery);
      return matchesName && matchesEmail && matchesPosition;
    });
  }, [talentBankEntries, talentFilters]);

  const filteredActiveEmployeeOptions = useMemo(() => {
    const query = historyEmployeeSearch.trim().toLowerCase();
    const sorted = [...activeEmployeeOptions].sort((a, b) =>
      (a.fullName || "").localeCompare(b.fullName || "", "pt-BR")
    );
    if (!query) {
      return sorted;
    }
    return sorted.filter((employee) =>
      (employee.fullName || "").toLowerCase().includes(query)
    );
  }, [activeEmployeeOptions, historyEmployeeSearch]);

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm({ ...defaultFormState });
    setFormError("");
    setAvailableUsers([]);
  };

  const loadAssignableUsers = useCallback(async () => {
    if (!canManage) {
      return;
    }
    setUsersLoading(true);
    try {
      const users = await HumanResourcesService.getAvailableUsers();
      setAvailableUsers(users);
    } catch (err) {
      console.error("Falha ao carregar usuários disponíveis", err);
      setAvailableUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [canManage]);

  const downloadEmployeesExcel = useCallback(
    async (params = {}, options = {}) => {
      const { closeModal = false, onError } = options;
      setDownloadingExcel(true);
      try {
        const { blob, fileName } = await HumanResourcesService.downloadEmployeesExcel(params);
        if (blob) {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName || "relatorio-colaboradores.xlsx";
          document.body.appendChild(link);
          link.click();
          link.remove();
              }
        if (closeModal) {
          setReportModalOpen(false);
        }
      } catch (err) {
        const message =
          err?.response?.data?.error || "Não foi possível gerar a planilha de colaboradores.";
        if (typeof onError === "function") {
          onError(message);
        } else {
          setError(message);
        }
      } finally {
        setDownloadingExcel(false);
      }
    },
    [setError, setReportModalOpen]
  );

  const downloadEmployeesPdf = useCallback(
    async (params = {}, options = {}) => {
      const { closeModal = false, onError } = options;
      setDownloadingPdf(true);
      try {
        const { blob, fileName } = await HumanResourcesService.downloadEmployeesPdf(params);
        if (blob) {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName || "relatorio-colaboradores.pdf";
          document.body.appendChild(link);
          link.click();
          link.remove();
              }
        if (closeModal) {
          setReportModalOpen(false);
        }
      } catch (err) {
        const message =
          err?.response?.data?.error || "Não foi possível gerar o PDF de colaboradores.";
        if (typeof onError === "function") {
          onError(message);
        } else {
          setError(message);
        }
      } finally {
        setDownloadingPdf(false);
      }
    },
    [setError, setReportModalOpen]
  );

  const handleReportDownload = useCallback(
    async (params = {}) => {
      setReportError("");
      await downloadEmployeesExcel(params, { onError: setReportError });
    },
    [downloadEmployeesExcel]
  );

  const buildPdfParams = useCallback(
    (params = {}) => ({ ...params, fields: selectedPdfFields.join(",") }),
    [selectedPdfFields]
  );

  const handlePdfFieldToggle = useCallback((fieldKey) => {
    setSelectedPdfFields((prev) => {
      if (prev.includes(fieldKey)) {
        return prev.length === 1 ? prev : prev.filter((key) => key !== fieldKey);
      }
      return [...prev, fieldKey];
    });
  }, []);

  const handleRefresh = useCallback(() => {
    loadEmployees();
    loadOverview();
    loadVacationControl();
    loadEmployeeHistory();
    loadTerminatedEmployees();
    loadTalentBankEntries();
    loadTalentJobs();
  }, [
    loadEmployees,
    loadOverview,
    loadVacationControl,
    loadEmployeeHistory,
    loadTerminatedEmployees,
    loadTalentBankEntries,
    loadTalentJobs,
  ]);

  const openReportModal = () => {
    setReportError("");
    setReportModalOpen(true);
  };

  const closeReportModal = () => {
    if (downloadingExcel || downloadingPdf) {
      return;
    }
    setReportModalOpen(false);
  };

  const openCreateModal = () => {
    setEditingId(null);
    setForm({
      ...defaultFormState,
      benefits: mergeCatalogBenefitsIntoRows(defaultFormState.benefits, benefitCatalog, defaultFormState.employmentType),
    });
    setFormError("");
    setModalOpen(true);
    if (canManage) {
      loadAssignableUsers();
    }
  };

  const openHistoryModal = (employee = null) => {
    setHistoryError("");
    setHistoryForm({
      ...defaultHistoryForm,
      employeeId: employee?.id ? String(employee.id) : "",
    });
    setHistoryEmployeeSearch(employee?.fullName || "");
    setHistoryModalOpen(true);
    loadActiveEmployeesForHistory();
  };

  const openEmployeeHistoryTab = () => {
    if (!editingId) return;
    setFocusedHistoryEmployeeId(editingId);
    setActiveTab("employeeHistory");
    closeModal();
  };

  const openImportModal = (type = "EMPLOYEES") => {
    const nextType = typeof type === "string" ? type : "EMPLOYEES";
    setImportError("");
    setImportSummary("");
    setImportType(nextType);
    setImportModalOpen(true);
  };

  const closeImportModal = () => {
    if (importingData) return;
    setImportModalOpen(false);
    setImportError("");
    setImportSummary("");
  };

  const closeHistoryModal = () => {
    if (historySaving) return;
    setHistoryModalOpen(false);
    setHistoryError("");
    setHistoryEmployeeSearch("");
    setHistoryForm({ ...defaultHistoryForm });
  };

  const openEditModal = (employee) => {
    setEditingId(employee.id);
    setForm({
      registrationNumber: employee.registrationNumber || "",
      fullName: employee.fullName || "",
      photoUrl: employee.photoUrl || "",
      socialName: employee.socialName || "",
      email: employee.email || "",
      phone: employee.phone || "",
      cpf: employee.cpf || "",
      rg: employee.rg || "",
      birthDate: normalizeDateInput(employee.birthDate) || "",
      gender: employee.gender || "",
      maritalStatus: employee.maritalStatus || "",
      department: employee.department || "",
      position: employee.position || "",
      employmentType: employee.employmentType || "",
      hireDate: normalizeDateInput(employee.hireDate) || "",
      terminationDate: normalizeDateInput(employee.terminationDate) || "",
      workShift: employee.workShift || "",
      salary: formatCurrencyInputValue(employee.salary),
      netSalary: formatCurrencyInputValue(employee.netSalary),
      benefits: mergeCatalogBenefitsIntoRows(
        Array.isArray(employee.benefits) && employee.benefits.length
          ? employee.benefits.map((benefit) => ({
              name: benefit.name || "",
              monthlyCost: formatCurrencyInputValue(benefit.monthlyCost),
            }))
          : [{ name: "", monthlyCost: "" }],
        benefitCatalog,
        employee.employmentType || "",
        benefitExclusions[String(employee.id)] || []
      ),
      fgtsPercentage: employee.fgtsPercentage === null || employee.fgtsPercentage === undefined ? "8" : String(employee.fgtsPercentage),
      employerInssExempt: employee.employerInssExempt ? "true" : "false",
      vacationDays:
        employee.vacationDays === null || employee.vacationDays === undefined
          ? ""
          : String(employee.vacationDays),
      notes: employee.notes || "",
      addressStreet: employee.addressStreet || "",
      addressNumber: employee.addressNumber || "",
      addressComplement: employee.addressComplement || "",
      addressNeighborhood: employee.addressNeighborhood || "",
      addressCity: employee.addressCity || "",
      addressState: employee.addressState || "",
      addressZipCode: employee.addressZipCode || "",
      emergencyContactName: employee.emergencyContactName || "",
      emergencyContactPhone: employee.emergencyContactPhone || "",
      status: employee.status || "ACTIVE",
      userId: employee.user?.id ? String(employee.user.id) : "",
      vacationDelegateUserIds: Array.isArray(employee.vacationDelegates)
        ? employee.vacationDelegates.map((delegate) => String(delegate.id))
        : [],
      employeeFiles: Array.isArray(employee.employeeFiles) && employee.employeeFiles.length
        ? employee.employeeFiles.map((file) => ({
            name: file.name || "",
            category: file.category || EMPLOYEE_DOCUMENT_CATEGORIES[0],
            dataUrl: file.dataUrl || "",
            contentPath: file.contentPath,
          }))
        : [{ name: "", category: EMPLOYEE_DOCUMENT_CATEGORIES[0], dataUrl: "" }],
    });
    setFormError("");
    setModalOpen(true);
    if (canManage) {
      loadAssignableUsers();
    }
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    if (name === "userId") {
      setForm((prev) => {
        if (!value) {
          return { ...prev, userId: "" };
        }
        const selectedUser = availableUsers.find((systemUser) => String(systemUser.id) === value);
        if (!selectedUser) {
          return { ...prev, userId: value };
        }

        return {
          ...prev,
          userId: value,
          fullName: selectedUser.name || prev.fullName,
          email: selectedUser.email || prev.email,
          birthDate: selectedUser.birthDate
            ? normalizeDateInput(selectedUser.birthDate)
            : prev.birthDate,
          department: selectedUser.sectorName || prev.department,
        };
      });
      return;
    }
    if (name === "employmentType") {
      setForm((prev) => ({
        ...prev,
        employmentType: value,
        benefits: mergeCatalogBenefitsIntoRows(prev.benefits, benefitCatalog, value, editingId ? benefitExclusions[String(editingId)] || [] : []),
      }));
      return;
    }
    if (name === "status" && value !== "VACATION") {
      setForm((prev) => ({ ...prev, status: value, vacationDelegateUserIds: [] }));
      return;
    }
    if (["phone", "emergencyContactPhone"].includes(name)) {
      setForm((prev) => ({ ...prev, [name]: maskPhone(value) }));
      return;
    }
    if (name === "cpf") {
      setForm((prev) => ({ ...prev, cpf: maskCpf(value) }));
      return;
    }
    if (["salary", "netSalary"].includes(name)) {
      setForm((prev) => ({ ...prev, [name]: maskCurrency(value) }));
      return;
    }
    if (name === "addressZipCode") {
      const maskedZipCode = maskCep(value);
      setForm((prev) => ({ ...prev, addressZipCode: maskedZipCode }));
      if (digitsOnly(maskedZipCode).length === 8) {
        fetchAddressByZipCode(maskedZipCode);
      }
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({ ...prev, photoUrl: String(reader.result || "") }));
    };
    reader.readAsDataURL(file);
  };

  const handleBenefitChange = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      benefits: (prev.benefits || []).map((benefit, currentIndex) =>
        currentIndex === index
          ? { ...benefit, [field]: field === "monthlyCost" ? maskCurrency(value) : value }
          : benefit
      ),
    }));
  };

  const addBenefit = () => {
    setForm((prev) => ({
      ...prev,
      benefits: [...(prev.benefits || []), { name: "", monthlyCost: "" }],
    }));
  };

  const removeBenefit = (index) => {
    setForm((prev) => {
      const removedBenefit = (prev.benefits || [])[index];
      const benefitSignature = getBenefitSignature(removedBenefit);
      if (editingId && benefitSignature !== "|") {
        setBenefitExclusions((current) => {
          const employeeKey = String(editingId);
          const currentIds = new Set(current[employeeKey] || []);
          currentIds.add(benefitSignature);
          return { ...current, [employeeKey]: Array.from(currentIds) };
        });
      }
      return {
        ...prev,
        benefits: (prev.benefits || []).filter((_, currentIndex) => currentIndex !== index),
      };
    });
  };

  const handleEmployeeFileChange = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      employeeFiles: (prev.employeeFiles || []).map((file, currentIndex) =>
        currentIndex === index ? { ...file, [field]: value } : file
      ),
    }));
  };

  const handleEmployeeDocumentUpload = async (index, file) => {
    if (!file) return;
    setFormError("");
    try {
      const uploaded = await HumanResourcesService.uploadEmployeeFile(file);
      setForm((prev) => ({
        ...prev,
        employeeFiles: (prev.employeeFiles || []).map((document, currentIndex) =>
          currentIndex === index
            ? { ...document, name: uploaded.name || file.name, dataUrl: uploaded.dataUrl || "", contentPath: "", previewBlob: file }
            : document
        ),
      }));
    } catch (err) {
      setFormError(err?.response?.data?.error || err?.message || "Não foi possível enviar o documento.");
    }
  };

  const openEmployeeFile = async (file, download = false) => {
    if (!download) {
      setPreviewEmployeeFile(file);
      return;
    }
    try {
      const result = await HumanResourcesService.fetchEmployeeFile(file);
      const objectUrl = URL.createObjectURL(result.blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = result.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (err) {
      setFormError(err?.response?.data?.error || err?.message || "Não foi possível abrir o documento.");
    }
  };

  const addEmployeeFile = () => {
    setForm((prev) => ({
      ...prev,
      employeeFiles: [...(prev.employeeFiles || []), { name: "", category: EMPLOYEE_DOCUMENT_CATEGORIES[0], dataUrl: "" }],
    }));
  };

  const removeEmployeeFile = (index) => {
    setForm((prev) => ({
      ...prev,
      employeeFiles: (prev.employeeFiles || []).filter((_, currentIndex) => currentIndex !== index),
    }));
  };

  const fetchAddressByZipCode = async (zipCode) => {
    const cep = digitsOnly(zipCode);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      if (data?.erro) return;
      setForm((prev) => ({
        ...prev,
        addressStreet: data.logradouro || prev.addressStreet,
        addressNeighborhood: data.bairro || prev.addressNeighborhood,
        addressCity: data.localidade || prev.addressCity,
        addressState: data.uf || prev.addressState,
      }));
    } catch (_) {
      // Mantém preenchimento manual caso a consulta externa falhe.
    }
  };

  const handleDelegateToggle = (userId) => {
    setForm((prev) => {
      const id = String(userId);
      const current = new Set(prev.vacationDelegateUserIds || []);
      if (current.has(id)) {
        current.delete(id);
      } else {
        current.add(id);
      }
      return { ...prev, vacationDelegateUserIds: Array.from(current) };
    });
  };

  const handleApplyFilters = (event) => {
    event.preventDefault();
    setFilters({ ...filterForm });
  };

  const handleResetFilters = () => {
    setFilterForm({ ...defaultFilters });
    setFilters({ ...defaultFilters });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canManage) {
      return;
    }
    setSaving(true);
    setFormError("");

    const payload = {
      registrationNumber: form.registrationNumber || null,
      fullName: form.fullName,
      photoUrl: form.photoUrl || null,
      socialName: form.socialName || null,
      email: form.email || null,
      phone: form.phone || null,
      cpf: form.cpf || null,
      rg: form.rg || null,
      birthDate: form.birthDate || null,
      gender: form.gender || null,
      maritalStatus: form.maritalStatus || null,
      department: form.department || null,
      position: form.position || null,
      employmentType: form.employmentType || null,
      hireDate: form.hireDate || null,
      terminationDate: form.terminationDate || null,
      workShift: form.workShift || null,
      salary: form.salary || null,
      netSalary: form.netSalary || null,
      benefits: (form.benefits || [])
        .filter((benefit) => benefit.name || benefit.monthlyCost)
        .map((benefit) => ({ name: benefit.name || null, monthlyCost: benefit.monthlyCost || null })),
      fgtsPercentage: form.fgtsPercentage || null,
      employerInssExempt: form.employerInssExempt === "true",
      employeeFiles: (form.employeeFiles || [])
        .filter((file) => file.name || file.dataUrl)
        .map((file) => ({ name: file.name || null, category: file.category || null, dataUrl: file.dataUrl || null })),
      vacationDays: form.vacationDays || null,
      notes: form.notes || null,
      addressStreet: form.addressStreet || null,
      addressNumber: form.addressNumber || null,
      addressComplement: form.addressComplement || null,
      addressNeighborhood: form.addressNeighborhood || null,
      addressCity: form.addressCity || null,
      addressState: form.addressState || null,
      addressZipCode: form.addressZipCode || null,
      emergencyContactName: form.emergencyContactName || null,
      emergencyContactPhone: form.emergencyContactPhone || null,
      status: form.status || "ACTIVE",
      userId: form.userId || null,
      vacationDelegateUserIds: Array.isArray(form.vacationDelegateUserIds)
        ? form.vacationDelegateUserIds
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id))
        : [],
    };

    try {
      if (editingId) {
        await HumanResourcesService.updateEmployee(editingId, payload);
      } else {
        await HumanResourcesService.createEmployee(payload);
      }
      closeModal();
      await loadEmployees();
      await loadOverview();
      await loadVacationControl();
    } catch (err) {
      const message = err?.response?.data?.error || "Não foi possível salvar o colaborador.";
      setFormError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleHistorySubmit = async (event) => {
    event.preventDefault();
    if (!canManage) return;
    setHistorySaving(true);
    setHistoryError("");
    try {
      const payload = {
        employeeId: historyForm.employeeId || null,
        type: historyForm.type,
        startDate: historyForm.startDate || null,
        endDate: historyForm.endDate || null,
        days: historyForm.days || null,
        notes: historyForm.notes || null,
      };
      if (historyForm.id) {
        await HumanResourcesService.updateEmployeeHistory(historyForm.id, payload);
      } else {
        await HumanResourcesService.createEmployeeHistory(payload);
      }
      closeHistoryModal();
      await loadEmployees();
      await loadOverview();
      await loadVacationControl();
      await loadEmployeeHistory();
    } catch (err) {
      setHistoryError(err?.response?.data?.error || "Não foi possível cadastrar o histórico.");
    } finally {
      setHistorySaving(false);
    }
  };

  const handleEditHistory = (entry) => {
    setHistoryError("");
    setHistoryEmployeeSearch(entry.employeeName || "");
    setHistoryForm({
      id: entry.id,
      employeeId: String(entry.employeeId || ""),
      type: entry.type || "VACATION",
      startDate: normalizeDateInput(entry.startDate) || "",
      endDate: normalizeDateInput(entry.endDate) || "",
      days: entry.days ? String(entry.days) : "",
      notes: entry.notes || "",
    });
    setHistoryModalOpen(true);
    loadActiveEmployeesForHistory();
  };

  const handleDeleteHistory = async (entry) => {
    if (!canManage) return;
    const confirmed = window.confirm("Deseja excluir este histórico?");
    if (!confirmed) {
      return;
    }
    try {
      await HumanResourcesService.deleteEmployeeHistory(entry.id);
      await loadEmployeeHistory();
      await loadVacationControl();
    } catch (err) {
      setError(err?.response?.data?.error || "Não foi possível remover o histórico.");
    }
  };

  const getEmployeeForUpdate = useCallback(
    async (employeeId) => {
      const fullEmployee = await HumanResourcesService.getEmployeeById(employeeId);
      if (!fullEmployee || !fullEmployee.id) {
        throw new Error("Colaborador não encontrado.");
      }
      return fullEmployee;
    },
    []
  );

  const openVacationEditModal = (entry = null) => {
    setVacationEditError("");
    setVacationEditForm({
      employeeId: entry ? String(entry.employeeId) : "",
      vacationDays:
        entry && entry.availableDays !== null && entry.availableDays !== undefined
          ? String(entry.availableDays)
          : "",
      acquisitionPeriodStart: normalizeDateInput(entry?.acquisitionPeriodStart) || "",
      acquisitionPeriodEnd: normalizeDateInput(entry?.acquisitionPeriodEnd) || "",
    });
    setVacationEditModalOpen(true);
  };

  const closeVacationEditModal = () => {
    if (vacationEditSaving) return;
    setVacationEditModalOpen(false);
    setVacationEditError("");
  };

  const handleVacationControlSubmit = async (event) => {
    event.preventDefault();
    if (!canManage) return;
    setVacationEditSaving(true);
    setVacationEditError("");
    try {
      if (!vacationEditForm.employeeId) {
        throw new Error("Selecione um colaborador.");
      }
      const employee = await getEmployeeForUpdate(Number(vacationEditForm.employeeId));
      await HumanResourcesService.updateEmployee(employee.id, {
        ...employee,
        vacationDays: vacationEditForm.vacationDays || null,
      });
      closeVacationEditModal();
      await loadEmployees();
      await loadVacationControl();
      await loadOverview();
    } catch (err) {
      setVacationEditError(err?.response?.data?.error || err.message || "Não foi possível atualizar férias.");
    } finally {
      setVacationEditSaving(false);
    }
  };

  const handleDeleteAcquisitionPeriod = async (entry) => {
    if (!canManage) return;
    const confirmed = window.confirm("Deseja excluir o período aquisitivo deste colaborador?");
    if (!confirmed) return;
    try {
      const employee = await getEmployeeForUpdate(entry.employeeId);
      await HumanResourcesService.updateEmployee(employee.id, {
        ...employee,
        hireDate: null,
      });
      await loadVacationControl();
    } catch (err) {
      setError(err?.response?.data?.error || "Não foi possível excluir o período aquisitivo.");
    }
  };

  const handleDelete = async (employee) => {
    if (!canManage) return;
    const confirmed = window.confirm(
      `Deseja remover o colaborador ${employee.fullName}? Essa ação não poderá ser desfeita.`
    );
    if (!confirmed) {
      return;
    }

    try {
      await HumanResourcesService.deleteEmployee(employee.id);
      await loadEmployees();
      await loadOverview();
      await loadVacationControl();
  } catch (err) {
      const message = err?.response?.data?.error || "Não foi possível remover o colaborador.";
      setError(message);
    }
  };

  const handleExportVacations = useCallback(() => {
    const rows = (employeeHistory || [])
      .filter((entry) => entry.type === "VACATION")
      .map((entry) => ({
        "Colaborador": entry.employeeName || "",
        "Data de início": entry.startDate || "",
        "Data de fim": entry.endDate || "",
        "Dias": entry.days || "",
        "Observações": entry.notes || "",
      }));
    const worksheet = XLSXUtils.json_to_sheet(rows.length ? rows : [{
      "Colaborador": "",
      "Data de início": "",
      "Data de fim": "",
      "Dias": "",
      "Observações": "",
    }]);
    worksheet["!cols"] = [{ wch: 32 }, { wch: 16 }, { wch: 16 }, { wch: 10 }, { wch: 42 }];
    const workbook = XLSXUtils.book_new();
    XLSXUtils.book_append_sheet(workbook, worksheet, "Ferias");
    writeXLSXFile(workbook, "ferias-colaboradores.xlsx", { bookType: "xlsx" });
  }, [employeeHistory]);

  const handleDownloadImportTemplate = useCallback(() => {
    try {
      const isEmployeesTemplate = importType === "EMPLOYEES";
      const isVacationTemplate = importType === "VACATIONS";

      if (isVacationTemplate) {
        const headers = VACATION_IMPORT_COLUMNS.map((column) => getImportColumnLabel(column));
        const worksheet = XLSXUtils.aoa_to_sheet([
          headers,
          ["", "", ""],
        ]);
        worksheet["!cols"] = [{ wch: 22 }, { wch: 18 }, { wch: 20 }];
        const workbook = XLSXUtils.book_new();
        XLSXUtils.book_append_sheet(workbook, worksheet, "Ferias");
        writeXLSXFile(workbook, "modelo-importacao-ferias.xlsx", { bookType: "xlsx" });
        setImportSummary("Modelo de férias baixado. Preencha somente CPF, data de início e quantidade de dias.");
        return;
      }

      const columns = isEmployeesTemplate ? EMPLOYEE_IMPORT_COLUMNS : HISTORY_IMPORT_COLUMNS;
      const templateRows = [columns.reduce((acc, column) => ({ ...acc, [getImportColumnLabel(column)]: "" }), {})];
      const worksheet = XLSXUtils.json_to_sheet(templateRows, {
        header: columns.map((column) => getImportColumnLabel(column)),
        skipHeader: false,
      });
      worksheet["!cols"] = columns.map((column) => ({ wch: Math.max(column.label.length + 4, 24) }));

      const instructionsSheet = XLSXUtils.json_to_sheet(
        columns.map((column) => ({
          Coluna: getImportColumnLabel(column),
          Obrigatorio: column.required ? "Sim" : "Não",
          Orientacao: column.instructions,
        }))
      );
      instructionsSheet["!cols"] = [{ wch: 34 }, { wch: 12 }, { wch: 80 }];

      const optionsRows = isEmployeesTemplate
        ? [
            { Tipo: "Status válidos", Opcoes: Object.values(STATUS_LABELS).join(" | ") },
            { Tipo: "Tipos de vínculo", Opcoes: EMPLOYMENT_TYPES.join(" | ") },
          ]
        : [{ Tipo: "Tipos de histórico", Opcoes: Object.values(HISTORY_TYPE_LABELS).join(" | ") }];
      const optionsSheet = XLSXUtils.json_to_sheet(optionsRows);
      optionsSheet["!cols"] = [{ wch: 30 }, { wch: 100 }];

      const workbook = XLSXUtils.book_new();
      XLSXUtils.book_append_sheet(workbook, worksheet, isEmployeesTemplate ? "Colaboradores" : "Historico");
      XLSXUtils.book_append_sheet(workbook, instructionsSheet, "Instrucoes");
      XLSXUtils.book_append_sheet(workbook, optionsSheet, "Opcoes");
      writeXLSXFile(
        workbook,
        isEmployeesTemplate ? "template-importacao-colaboradores.xlsx" : "template-importacao-historico.xlsx",
        { bookType: "xlsx" }
      );
      setImportSummary("Template baixado com sucesso.");
    } catch (err) {
      setImportError("Não foi possível gerar o template de importação.");
    }
  }, [importType]);

  const normalizeStatus = (value) => {
    if (!value) return "";
    const normalized = normalizeImportText(value);
    const entries = Object.entries(STATUS_LABELS);
    const match = entries.find(([key, label]) => {
      return normalizeImportText(key) === normalized || normalizeImportText(label) === normalized;
    });
    return match ? match[0] : "";
  };

  const normalizeHistoryType = (value) => {
    if (!value) return "";
    const normalized = normalizeImportText(value);
    const entries = Object.entries(HISTORY_TYPE_LABELS);
    const match = entries.find(([key, label]) => {
      return normalizeImportText(key) === normalized || normalizeImportText(label) === normalized;
    });
    return match ? match[0] : "";
  };

  const handleImportFileChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;

      setImportError("");
      setImportSummary("");
      setImportingData(true);

      try {
        const buffer = await file.arrayBuffer();
        const workbook = readXLSX(buffer, { type: "array", cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        if (!worksheet) {
          throw new Error("Planilha inválida: aba de dados não encontrada.");
        }

        const rows = XLSXUtils.sheet_to_json(worksheet, { defval: "" });
        if (!rows.length) {
          throw new Error("A planilha está vazia.");
        }

        const toPayload = (columns, row) => {
          const normalizedRow = {};
          Object.entries(row).forEach(([header, value]) => {
            normalizedRow[normalizeImportHeader(header)] = value;
          });
          return columns.reduce((acc, column) => {
            const columnHeader = normalizeImportHeader(column.label);
            const requiredHeader = normalizeImportHeader(getImportColumnLabel(column));
            acc[column.key] = coerceImportValue(
              normalizedRow[requiredHeader] ?? normalizedRow[columnHeader]
            );
            return acc;
          }, {});
        };

        let successCount = 0;
        const errors = [];

        if (importType === "EMPLOYEES") {
          for (let index = 0; index < rows.length; index += 1) {
            const payload = toPayload(EMPLOYEE_IMPORT_COLUMNS, rows[index]);
            if (!payload.fullName) {
              errors.push(`Linha ${index + 2}: "Nome completo" é obrigatório.`);
              continue;
            }
            const normalizedStatus = normalizeStatus(payload.status);
            if (!normalizedStatus) {
              errors.push(`Linha ${index + 2}: status inválido.`);
              continue;
            }
            await HumanResourcesService.createEmployee({
              registrationNumber: payload.registrationNumber || null,
              fullName: payload.fullName,
              email: payload.email || null,
              phone: payload.phone || null,
              department: payload.department || null,
              position: payload.position || null,
              employmentType: payload.employmentType || null,
              hireDate: payload.hireDate || null,
              salary: payload.salary || null,
              vacationDays: payload.vacationDays || null,
              status: normalizedStatus,
              notes: payload.notes || null,
            });
            successCount += 1;
          }
        } else {
          const employeeList = await HumanResourcesService.listEmployees();
          const employeeByRegistration = new Map(
            employeeList
              .filter((employee) => employee.registrationNumber)
              .map((employee) => [normalizeImportText(employee.registrationNumber), employee])
          );
          const employeeByEmail = new Map(
            employeeList
              .filter((employee) => employee.email)
              .map((employee) => [normalizeImportText(employee.email), employee])
          );
          const employeeByName = new Map(
            employeeList
              .filter((employee) => employee.fullName)
              .map((employee) => [normalizeImportText(employee.fullName), employee])
          );
          const employeeByCpf = new Map(
            employeeList
              .filter((employee) => normalizeImportCpf(employee.cpf))
              .map((employee) => [normalizeImportCpf(employee.cpf), employee])
          );

          for (let index = 0; index < rows.length; index += 1) {
            const isVacationImport = importType === "VACATIONS";
            const payload = toPayload(isVacationImport ? VACATION_IMPORT_COLUMNS : HISTORY_IMPORT_COLUMNS, rows[index]);
            const employee = isVacationImport
              ? employeeByCpf.get(normalizeImportCpf(payload.cpf))
              : (
                  employeeByRegistration.get(normalizeImportText(payload.registrationNumber)) ||
                  employeeByEmail.get(normalizeImportText(payload.employeeEmail)) ||
                  employeeByName.get(normalizeImportText(payload.employeeName))
                );
            if (!employee?.id) {
              errors.push(
                isVacationImport
                  ? `Linha ${index + 2}: CPF "${String(payload.cpf || "").trim() || "não informado"}" não encontrado no cadastro de colaboradores.`
                  : `Linha ${index + 2}: colaborador não localizado.`,
              );
              continue;
            }

            if (isVacationImport) {
              const vacationDays = Number(payload.days);
              if (!payload.startDate) {
                errors.push(`Linha ${index + 2}: informe "Data de início das férias".`);
                continue;
              }
              if (!Number.isInteger(vacationDays) || vacationDays <= 0) {
                errors.push(`Linha ${index + 2}: informe uma "Quantidade de dias" válida.`);
                continue;
              }
              await HumanResourcesService.createEmployeeHistory({
                employeeId: employee.id,
                type: "VACATION",
                startDate: payload.startDate,
                endDate: null,
                days: vacationDays,
                notes: payload.notes || null,
              });
              successCount += 1;
              continue;
            }

            const normalizedType = normalizeHistoryType(payload.type);
            if (!normalizedType) {
              errors.push(`Linha ${index + 2}: tipo de histórico inválido.`);
              continue;
            }
            if (normalizedType === "VACATION" && !payload.days) {
              errors.push(`Linha ${index + 2}: informe "Dias" para férias.`);
              continue;
            }

            await HumanResourcesService.createEmployeeHistory({
              employeeId: employee.id,
              type: normalizedType,
              startDate: payload.startDate || null,
              endDate: payload.endDate || null,
              days: payload.days || null,
              notes: payload.notes || null,
            });
            successCount += 1;
          }
        }

        await loadEmployees();
        await loadOverview();
        await loadVacationControl();
        await loadEmployeeHistory();

        if (errors.length) {
          setImportError(
            `${successCount} registro(s) importado(s). ${errors.length} linha(s) com erro: ${errors
              .slice(0, 4)
              .join(" | ")}${errors.length > 4 ? " ..." : ""}`
          );
        } else {
          setImportSummary(`${successCount} registro(s) importado(s) com sucesso.`);
        }
      } catch (err) {
        setImportError(err?.message || "Não foi possível importar a planilha.");
      } finally {
        setImportingData(false);
      }
    },
    [importType, loadEmployeeHistory, loadEmployees, loadOverview, loadVacationControl]
  );

  const summaryData = overview?.summary ?? defaultOverview.summary;
  const upcomingBirthdays = overview?.upcomingBirthdays ?? [];
  const withoutSystemAccessList = overview?.withoutSystemAccess ?? [];
  const vacationControlList = vacationControl ?? [];
  const activeEmployeeRows = employees.filter((employee) => employee.status !== "TERMINATED");
  const adjustedSummaryData = useMemo(() => {
    const catalogAwareBenefitsMonthlyCost = activeEmployeeRows.reduce((total, employee) => {
      const benefitRows = mergeCatalogBenefitsIntoRows(
        Array.isArray(employee.benefits)
          ? employee.benefits.map((benefit) => ({
              name: benefit.name || "",
              monthlyCost: formatCurrencyInputValue(benefit.monthlyCost),
            }))
          : [],
        benefitCatalog,
        employee.employmentType || "",
        benefitExclusions[String(employee.id)] || []
      );
      return total + sumBenefitRowsMonthlyCost(benefitRows);
    }, 0);
    const backendBenefitsMonthlyCost = Number(summaryData.benefitsMonthlyCost || 0);
    const benefitsMonthlyCost = Math.max(backendBenefitsMonthlyCost, catalogAwareBenefitsMonthlyCost);
    return {
      ...summaryData,
      benefitsMonthlyCost,
      hrMonthlyCost: Number(summaryData.hrMonthlyCost || 0) + Math.max(0, benefitsMonthlyCost - backendBenefitsMonthlyCost),
    };
  }, [activeEmployeeRows, benefitCatalog, benefitExclusions, summaryData]);

  const loadingSteps = useMemo(
    () => [
      { isLoading: loading, label: "Carregando colaboradores" },
      { isLoading: overviewLoading, label: "Atualizando indicadores" },
      { isLoading: vacationControlLoading, label: "Carregando controle de férias" },
      { isLoading: historyLoading, label: "Carregando histórico" },
      { isLoading: terminatedLoading, label: "Carregando desligados" },
      { isLoading: talentLoading, label: "Carregando banco de talentos" },
      { isLoading: talentJobsLoading, label: "Carregando vagas" },
    ],
    [
      loading,
      overviewLoading,
      vacationControlLoading,
      historyLoading,
      terminatedLoading,
      talentLoading,
      talentJobsLoading,
    ]
  );
  const activeLoadingStep = loadingSteps.find((step) => step.isLoading);
  const humanResourcesLoadingProgress = Math.max(
    8,
    Math.round(
      ((loadingSteps.length - loadingSteps.filter((step) => step.isLoading).length) / loadingSteps.length) * 100
    )
  );

  const summaryHighlights = useMemo(
    () => [
      {
        id: "total",
        label: "Colaboradores",
        value: overviewLoading ? "…" : adjustedSummaryData.total,
        icon: FaUsers,
        hint: "",
        cardClassName:
          "border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-indigo-100/70 hover:border-indigo-300",
        labelClassName: "text-indigo-600",
        valueClassName: "text-indigo-950",
        hintClassName: "text-indigo-700/80",
        iconClassName: "bg-indigo-100 text-indigo-600 group-hover:bg-indigo-200 group-hover:text-indigo-700",
      },
      {
        id: "active",
        label: "Ativos",
        value: overviewLoading ? "…" : adjustedSummaryData.active,
        icon: FaUserTie,
        hint: "",
        cardClassName:
          "border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-emerald-100/70 hover:border-emerald-300",
        labelClassName: "text-emerald-700",
        valueClassName: "text-emerald-950",
        hintClassName: "text-emerald-700/80",
        iconClassName:
          "bg-emerald-100 text-emerald-600 group-hover:bg-emerald-200 group-hover:text-emerald-700",
      },
      {
        id: "vacation",
        label: "Em férias",
        value: overviewLoading ? "…" : adjustedSummaryData.vacation,
        icon: FaUmbrellaBeach,
        hint: "",
        cardClassName:
          "border-sky-200 bg-gradient-to-br from-sky-50 via-white to-cyan-100/70 hover:border-sky-300",
        labelClassName: "text-sky-700",
        valueClassName: "text-sky-950",
        hintClassName: "text-sky-700/80",
        iconClassName: "bg-sky-100 text-sky-600 group-hover:bg-sky-200 group-hover:text-sky-700",
      },
      {
        id: "onLeave",
        label: "Licenças",
        value: overviewLoading ? "…" : adjustedSummaryData.onLeave,
        icon: FaUserClock,
        hint: "",
        cardClassName:
          "border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-100/70 hover:border-amber-300",
        labelClassName: "text-amber-700",
        valueClassName: "text-amber-950",
        hintClassName: "text-amber-700/80",
        iconClassName:
          "bg-amber-100 text-amber-600 group-hover:bg-amber-200 group-hover:text-amber-700",
      },
      {
        id: "hrMonthlyCost",
        label: "Custo RH Mensal",
        value: overviewLoading ? "…" : formatCurrency(adjustedSummaryData.hrMonthlyCost),
        icon: FaMoneyBillWave,
        hint: "",
        cardClassName:
          "border-teal-200 bg-gradient-to-br from-teal-50 via-white to-emerald-100/70 hover:border-teal-300",
        labelClassName: "text-teal-700",
        valueClassName: "text-teal-950",
        hintClassName: "text-teal-700/80",
        iconClassName: "bg-teal-100 text-teal-600 group-hover:bg-teal-200 group-hover:text-teal-700",
      },
      {
        id: "benefitsMonthlyCost",
        label: "Custo Benefícios Mensal",
        value: overviewLoading ? "…" : formatCurrency(adjustedSummaryData.benefitsMonthlyCost),
        icon: FaGift,
        hint: "",
        cardClassName:
          "border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 via-white to-pink-100/70 hover:border-fuchsia-300",
        labelClassName: "text-fuchsia-700",
        valueClassName: "text-fuchsia-950",
        hintClassName: "text-fuchsia-700/80",
        iconClassName:
          "bg-fuchsia-100 text-fuchsia-600 group-hover:bg-fuchsia-200 group-hover:text-fuchsia-700",
      },
    ],
    [overviewLoading, adjustedSummaryData]
  );

  const vacationControlSection = (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <header className="flex flex-col gap-1 border-b border-slate-100 pb-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
          <FaCalendarCheck className="text-emerald-600" />
          Controle de férias
        </div>
        <div className="flex items-center gap-2">
          <p className="text-xs text-slate-400">
            Períodos aquisitivos, vencimentos de gozo e saldos do colaborador.
          </p>
          {canManage && (
            <>
              <button
                type="button"
                onClick={openHistoryModal}
                className="inline-flex items-center gap-1 rounded-md border border-violet-200 px-2 py-1 text-xs font-semibold text-violet-700"
              >
                <FaClipboardList /> Cadastrar histórico
              </button>
              <button
                type="button"
                onClick={() => openImportModal("VACATIONS")}
                className="inline-flex items-center gap-1 rounded-md border border-sky-200 px-2 py-1 text-xs font-semibold text-sky-700"
              >
                <FaFileImport /> Importar férias
              </button>
              <button type="button" onClick={handleExportVacations} className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700"><FaFileExcel /> Exportar</button>
              <button
                type="button"
                onClick={() => openVacationEditModal()}
                className="inline-flex items-center gap-1 rounded-md border border-emerald-200 px-2 py-1 text-xs font-semibold text-emerald-700"
              >
                <FaUserPlus /> Adicionar saldo/período
              </button>
            </>
          )}
        </div>
      </header>
      <div className="mt-4 space-y-3">
        {vacationControlError && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
            {vacationControlError}
          </div>
        )}

        {!vacationControlError && (
          <div className="overflow-x-auto">
            {vacationControlLoading ? (
              <p className="text-sm text-slate-500">Carregando painel de férias...</p>
            ) : vacationControlList.length === 0 ? (
              <p className="text-sm text-slate-500">
                Nenhum colaborador cadastrado no controle de férias até o momento.
              </p>
            ) : (
              <table className="min-w-full divide-y divide-slate-200">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3">Colaborador</th>
                    <th className="px-4 py-3">Período aquisitivo</th>
                    <th className="px-4 py-3">Prazo de concessão</th>
                    <th className="px-4 py-3">Próximo período</th>
                    <th className="px-4 py-3">Dias</th>
                    {canManage && <th className="px-4 py-3 text-right">Ações</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {vacationControlList.map((entry) => (
                    <tr key={entry.employeeId} className="align-top text-sm text-slate-700">
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-slate-900">{entry.employeeName}</span>
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                STATUS_STYLES[entry.status] || "bg-slate-100 text-slate-600"
                              }`}
                            >
                              {STATUS_LABELS[entry.status] || entry.status}
                            </span>
                          </div>
                          <span className="text-xs text-slate-500">
                            {entry.department
                              ? `${entry.department}${entry.position ? ` • ${entry.position}` : ""}`
                              : entry.position || "Sem departamento"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-slate-800">
                            {formatDateRange(entry.acquisitionPeriodStart, entry.acquisitionPeriodEnd)}
                          </span>
                          {entry.hireDate && (
                            <span className="text-xs text-slate-500">
                              Admissão: {formatDateOnly(entry.hireDate)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-slate-800">
                            {entry.concessionPeriodEnd
                              ? `Vence em ${formatDateOnly(entry.concessionPeriodEnd)}`
                              : "Prazo não calculado"}
                          </span>
                          {entry.daysUntilConcessionEnds !== null &&
                            entry.daysUntilConcessionEnds !== undefined && (
                              <span className="text-xs text-amber-700">
                                {entry.daysUntilConcessionEnds === 0
                                  ? "Prazo encerra hoje"
                                  : `Faltam ${formatDaysLabel(entry.daysUntilConcessionEnds)}`}
                              </span>
                            )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-slate-800">
                            {entry.nextAcquisitionEnd
                              ? formatDateOnly(entry.nextAcquisitionEnd)
                              : "Data não calculada"}
                          </span>
                          {entry.daysUntilNextAcquisitionEnd !== null &&
                            entry.daysUntilNextAcquisitionEnd !== undefined && (
                              <span className="text-xs text-slate-500">
                                Próximo período em {formatDaysLabel(entry.daysUntilNextAcquisitionEnd)}
                              </span>
                            )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">
                            Direito: {formatDaysLabel(entry.entitledDays)}
                          </span>
                          <span className="rounded-full bg-sky-100 px-2 py-1 text-xs font-semibold text-sky-700">
                            Usados: {formatDaysLabel(entry.usedDays)}
                          </span>
                          <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">
                            Saldo: {formatDaysLabel(entry.availableDays)}
                          </span>
                        </div>
                      </td>
                      {canManage && (
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openVacationEditModal(entry)}
                              className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600"
                            >
                              <FaEdit /> Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteAcquisitionPeriod(entry)}
                              className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2 py-1 text-xs font-medium text-rose-600"
                            >
                              <FaTrash /> Excluir período
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </section>
  );


  const handleTalentJobFormChange = (event) => {
    const { name, value } = event.target;
    setTalentJobForm((prev) => ({ ...prev, [name]: name === "salary" ? maskCurrency(value) : value }));
  };

  const handleTalentJobBenefitChange = (index, value) => {
    setTalentJobForm((prev) => ({
      ...prev,
      benefits: (prev.benefits || []).map((benefit, benefitIndex) => (benefitIndex === index ? value : benefit)),
    }));
  };

  const addTalentJobBenefit = () => {
    setTalentJobForm((prev) => ({ ...prev, benefits: [...(prev.benefits || []), ""] }));
  };

  const removeTalentJobBenefit = (index) => {
    setTalentJobForm((prev) => ({
      ...prev,
      benefits: (prev.benefits || []).filter((_, benefitIndex) => benefitIndex !== index).length
        ? (prev.benefits || []).filter((_, benefitIndex) => benefitIndex !== index)
        : [""],
    }));
  };

  const resetTalentJobForm = () => {
    setTalentJobForm({ ...defaultTalentJobForm, publicationDate: new Date().toISOString().slice(0, 10), benefits: [""] });
    setTalentJobEditingId(null);
    setTalentJobError("");
  };

  const handleTalentJobSubmit = async (event) => {
    event.preventDefault();
    setTalentJobError("");
    setTalentJobSaving(true);
    try {
      const payload = {
        ...talentJobForm,
        benefits: (talentJobForm.benefits || []).map((benefit) => String(benefit || "").trim()).filter(Boolean),
      };
      const saved = talentJobEditingId
        ? await HumanResourcesService.updateTalentJob(talentJobEditingId, payload)
        : await HumanResourcesService.createTalentJob(payload);
      setTalentJobs((prev) => {
        if (talentJobEditingId) return prev.map((job) => (Number(job.id) === Number(saved.id) ? saved : job));
        return [saved, ...prev];
      });
      resetTalentJobForm();
    } catch (err) {
      setTalentJobError(err?.response?.data?.error || err?.message || "Não foi possível salvar a vaga.");
    } finally {
      setTalentJobSaving(false);
    }
  };

  const handleEditTalentJob = (job) => {
    setTalentJobEditingId(job.id);
    setTalentJobForm({
      name: job.name || "",
      status: TALENT_JOB_STATUS_OPTIONS.includes(job.status) ? job.status : "Rascunho",
      publicationDate: job.publicationDate || new Date().toISOString().slice(0, 10),
      description: job.description || "",
      requirements: job.requirements || "",
      salary: job.salary || "",
      benefits: (job.benefits || []).length ? job.benefits : [""],
      workload: job.workload || "",
      notes: job.notes || "",
    });
    setTalentJobError("");
  };

  const handleDeleteTalentJob = async (jobId) => {
    const confirmed = window.confirm("Deseja excluir esta vaga?");
    if (!confirmed) return;
    try {
      await HumanResourcesService.deleteTalentJob(jobId);
      setTalentJobs((prev) => prev.filter((job) => Number(job.id) !== Number(jobId)));
      if (Number(talentJobEditingId) === Number(jobId)) resetTalentJobForm();
    } catch (err) {
      setTalentJobError(err?.response?.data?.error || "Não foi possível excluir a vaga.");
    }
  };

  const handleTalentFormChange = (event) => {
    const { name, value } = event.target;
    setTalentForm((prev) => ({ ...prev, [name]: name === "phone" ? maskPhone(value) : value }));
  };

  const handleTalentFilterChange = (event) => {
    const { name, value } = event.target;
    setTalentFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleResetTalentFilters = () => {
    setTalentFilters({ ...defaultTalentFilters });
  };

  const handleTalentCurriculumChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setTalentError("");
    setTalentCurriculumFile((prev) => [...(prev || []), ...files]);
    event.target.value = "";
  };

  const resetTalentForm = () => {
    setTalentForm({ ...defaultTalentForm });
    setTalentCurriculumFile([]);
    setTalentEditingId(null);
    setTalentError("");
  };

  const handleTalentSubmit = async (event) => {
    event.preventDefault();
    setTalentError("");

    const currentEntry = talentEditingId
      ? talentBankEntries.find((entry) => Number(entry.id) === Number(talentEditingId))
      : null;

    if (!talentEditingId && (talentCurriculumFile || []).length === 0) {
      setTalentError("Selecione um ou mais arquivos.");
      return;
    }

    setTalentSaving(true);
    try {
      const normalizedFiles = await Promise.all((talentCurriculumFile || []).map(async (file) => ({
        id: `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2)}`,
        name: file.name,
        type: file.type || "Documento",
        size: file.size || null,
        dataUrl: await readTalentFileAsDataUrl(file),
        uploadedAt: new Date().toISOString(),
      })));

      const payload = {
        ...talentForm,
        files: [...(currentEntry?.files || []), ...normalizedFiles],
      };

      if (talentEditingId) {
        await HumanResourcesService.updateTalentBankEntry(talentEditingId, payload);
      } else {
        await HumanResourcesService.createTalentBankEntry(payload);
      }

      await loadTalentBankEntries();
      resetTalentForm();
    } catch (err) {
      setTalentError(err?.response?.data?.error || err?.message || "Não foi possível salvar o currículo no Banco de Talentos.");
    } finally {
      setTalentSaving(false);
    }
  };

  const handleEditTalentEntry = (entry) => {
    setTalentEditingId(entry.id);
    setTalentForm({
      candidateName: entry.candidateName || "",
      email: entry.email || "",
      phone: entry.phone || "",
      position: entry.position || "",
      notes: entry.notes || "",
    });
    setTalentCurriculumFile([]);
  };

  const handleDeleteTalentEntry = async (entryId) => {
    const confirmed = window.confirm("Deseja excluir este cadastro do Banco de Talentos?");
    if (!confirmed) return;
    try {
      await HumanResourcesService.deleteTalentBankEntry(entryId);
      setTalentBankEntries((prev) => prev.filter((entry) => entry.id !== entryId));
    } catch (err) {
      setTalentError(err?.response?.data?.error || "Não foi possível excluir o cadastro.");
      return;
    }
    if (viewingTalentEntry?.id === entryId) {
      setViewingTalentEntry(null);
    }
    if (talentEditingId === entryId) {
      resetTalentForm();
    }
  };

  const handleRemovePendingTalentFile = (indexToRemove) => {
    setTalentCurriculumFile((prev) => (prev || []).filter((_, index) => index !== indexToRemove));
  };

  const handleDeleteTalentFile = async (entryId, fileId) => {
    const entry = talentBankEntries.find((item) => Number(item.id) === Number(entryId));
    if (!entry) return;
    const updatedEntry = { ...entry, files: (entry.files || []).filter((file) => file.id !== fileId) };
    try {
      const saved = await HumanResourcesService.updateTalentBankEntry(entryId, updatedEntry);
      setTalentBankEntries((prev) => prev.map((item) => (Number(item.id) === Number(entryId) ? saved : item)));
      setViewingTalentEntry((prev) => (prev && Number(prev.id) === Number(entryId) ? saved : prev));
    } catch (err) {
      setTalentError(err?.response?.data?.error || "Não foi possível excluir o arquivo.");
    }
  };

  const handleDownloadTalentFile = (fileEntry) => {
    if (!fileEntry?.dataUrl) return;
    const url = fileEntry.dataUrl;
    const link = document.createElement("a");
    link.href = url;
    link.download = fileEntry.name || "curriculo";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const columnCount = canManage ? 9 : 8;

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-3 rounded-2xl bg-blue-950 px-4 py-4 text-white shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-white">
            <FaIdBadge className="text-white" />
            Recursos Humanos
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-white/20"
          >
            <FaSync className="text-white/80" /> Atualizar
          </button>
        </div>
      </header>

      {activeLoadingStep && (
        <HumanResourcesLoadingBar
          label={activeLoadingStep.label}
          progress={humanResourcesLoadingProgress}
        />
      )}

      <nav className="flex flex-wrap gap-2">
        {TAB_ITEMS.map((tab) => {
          const TabIcon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-100 ${
                isActive
                  ? "bg-slate-900 text-white shadow"
                  : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300"
              }`}
            >
              <TabIcon />
              {tab.label}
            </button>
          );
        })}
      </nav>

      {activeTab === "overview" && (
        <div className="space-y-8">

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Ações rápidas
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {canManage && (
              <button
                type="button"
                onClick={openCreateModal}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700"
              >
                <FaUserPlus /> Cadastrar colaborador
              </button>
            )}
            {canManage && (
              <button
                type="button"
                onClick={openImportModal}
                className="inline-flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700 shadow-sm transition hover:border-sky-300 hover:bg-sky-100"
              >
                <FaFileImport /> Importar Excel
              </button>
            )}
            <button
              type="button"
              onClick={openReportModal}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
            >
              <FaClipboardList /> Relatórios
            </button>
          </div>
        </div>
      </section>

      {overviewError && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
          {overviewError}
        </div>
      )}

      <nav className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        {OVERVIEW_QUICK_TABS.map((tab) => {
          const TabIcon = tab.icon;
          const isActive = overviewQuickTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setOverviewQuickTab(tab.id)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
                isActive
                  ? "bg-blue-950 text-white shadow"
                  : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300"
              }`}
            >
              <TabIcon /> {tab.label}
            </button>
          );
        })}
      </nav>

      {overviewQuickTab === "summary" && (
        <>
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {summaryHighlights.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.id}
              className={`group rounded-2xl border p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${card.cardClassName}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className={`text-xs font-semibold uppercase tracking-wide ${card.labelClassName}`}>
                    {card.label}
                  </p>
                  <p className={`mt-3 text-3xl font-semibold ${card.valueClassName}`}>{card.value}</p>
                </div>
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-full transition ${card.iconClassName}`}
                >
                  <Icon />
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <form
        onSubmit={handleApplyFilters}
        className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:grid-cols-2 xl:grid-cols-5"
      >
        <div className="md:col-span-2">
          <label htmlFor="hr-filter-search" className="mb-1 block text-sm font-medium text-slate-600">
            Busca rápida
          </label>
          <div className="relative">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                id="hr-filter-search"
                name="search"
                type="search"
                placeholder="Nome, CPF, e-mail ou matrícula"
                value={filterForm.search}
                onChange={(event) =>
                  setFilterForm((prev) => ({ ...prev, search: event.target.value }))
                }
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
              />
          </div>
        </div>
        <div>
          <label htmlFor="hr-filter-status" className="mb-1 block text-sm font-medium text-slate-600">
            Status
          </label>
          <select
            id="hr-filter-status"
            value={filterForm.status}
            onChange={(event) =>
              setFilterForm((prev) => ({ ...prev, status: event.target.value }))
            }
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="hr-filter-employment" className="mb-1 block text-sm font-medium text-slate-600">
            Tipo de vínculo
          </label>
          <select
            id="hr-filter-employment"
            value={filterForm.employmentType}
            onChange={(event) =>
              setFilterForm((prev) => ({ ...prev, employmentType: event.target.value }))
            }
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
          >
            <option value="ALL">Todos</option>
            {EMPLOYMENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="hr-filter-department" className="mb-1 block text-sm font-medium text-slate-600">
            Departamento
          </label>
          <input
            id="hr-filter-department"
            type="text"
            list="hr-department-suggestions"
            placeholder="Todos"
            value={filterForm.department}
            onChange={(event) =>
              setFilterForm((prev) => ({ ...prev, department: event.target.value }))
            }
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
          />
          <datalist id="hr-department-suggestions">
            {departmentSuggestions.map((department) => (
              <option key={department} value={department} />
            ))}
          </datalist>
        </div>
        <div className="flex items-end gap-3 md:col-span-2 xl:col-span-5">
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
          >
            Aplicar
          </button>
          <button
            type="button"
            onClick={handleResetFilters}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
          >
            Limpar filtros
          </button>
        </div>
      </form>

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                Foto
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                Colaborador
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                Departamento / Cargo
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                Vínculo
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                Admissão
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                Salário
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                Usuário do sistema
              </th>
              {canManage && (
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Ações
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {loading ? (
              <tr>
                <td colSpan={columnCount} className="px-6 py-12 text-center text-sm text-slate-500">
                  Carregando colaboradores...
                </td>
              </tr>
            ) : activeEmployeeRows.length === 0 ? (
              <tr>
                <td colSpan={columnCount} className="px-6 py-12 text-center text-sm text-slate-500">
                  Nenhum colaborador encontrado com os filtros selecionados.
                </td>
              </tr>
            ) : (
              activeEmployeeRows.map((employee) => (
                <tr key={employee.id} onClick={() => openEditModal(employee)} className="cursor-pointer hover:bg-slate-50">
                  <td className="px-6 py-4">
                    {employee.photoUrl ? (
                      <img
                        src={employee.photoUrl}
                        alt={`Foto de ${employee.fullName}`}
                        className="h-12 w-12 rounded-2xl object-cover ring-2 ring-slate-100"
                      />
                    ) : (
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-sm font-semibold text-slate-500 ring-2 ring-slate-100">
                        {getInitials(employee.fullName)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-slate-900">
                        {employee.fullName}
                      </span>
                      <span className="mt-1 text-xs text-slate-500">
                        {employee.registrationNumber
                          ? `Matrícula ${employee.registrationNumber}`
                          : "Sem matrícula"}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-700">
                      {formatDepartment(employee.department, employee.position)}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700">
                    {formatEmploymentType(employee.employmentType)}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                        STATUS_STYLES[employee.status] || "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {STATUS_LABELS[employee.status] || employee.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700">
                    {employee.hireDate ? formatDateOnly(employee.hireDate) : "—"}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700">
                    {formatCurrency(employee.salary)}
                  </td>
                  <td className="px-6 py-4">
                    {employee.user ? (
                      <div className="flex flex-col text-sm text-slate-700">
                        <span className="font-medium">{employee.user.name}</span>
                        <span className="mt-0.5 text-xs text-slate-500">{employee.user.email}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-slate-400">Sem acesso</span>
                    )}
                  </td>
                  {canManage && (
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openEditModal(employee);
                          }}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition hover:border-emerald-300 hover:text-emerald-700"
                        >
                          <FaEdit /> Editar
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDelete(employee);
                          }}
                          className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-medium text-rose-600 shadow-sm transition hover:border-rose-300 hover:text-rose-700"
                        >
                          <FaTrash /> Excluir
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

        </>
      )}

      {overviewQuickTab === "terminatedEmployees" && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <header className="flex items-center gap-2 border-b border-slate-100 pb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
            <FaBan className="text-rose-600" />
            Colaboradores desligados
          </header>
          <div className="mt-4 overflow-x-auto">
            {terminatedLoading ? (
              <p className="text-sm text-slate-500">Carregando colaboradores desligados...</p>
            ) : terminatedEmployees.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhum colaborador desligado cadastrado.</p>
            ) : (
              <table className="min-w-full divide-y divide-slate-200">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3">Colaborador</th>
                    <th className="px-4 py-3">Departamento / Cargo</th>
                    <th className="px-4 py-3">Admissão</th>
                    <th className="px-4 py-3">Desligamento</th>
                    {canManage && <th className="px-4 py-3 text-right">Ações</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
                  {terminatedEmployees.map((employee) => (
                    <tr key={employee.id}>
                      <td className="px-4 py-3 font-medium text-slate-900">{employee.fullName}</td>
                      <td className="px-4 py-3">{formatDepartment(employee.department, employee.position)}</td>
                      <td className="px-4 py-3">{employee.hireDate ? formatDateOnly(employee.hireDate) : "—"}</td>
                      <td className="px-4 py-3">{employee.terminationDate ? formatDateOnly(employee.terminationDate) : "—"}</td>
                      {canManage && (
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => openEditModal(employee)}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition hover:border-emerald-300 hover:text-emerald-700"
                          >
                            <FaEdit /> Editar
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}

      {overviewQuickTab === "birthdays" && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <header className="flex items-center gap-2 border-b border-slate-100 pb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
            <FaBirthdayCake className="text-rose-500" />
            Aniversariantes nos próximos 60 dias
          </header>
          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {overviewLoading ? (
              <p className="text-sm text-slate-500">Carregando aniversariantes...</p>
            ) : upcomingBirthdays.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhum colaborador faz aniversário nos próximos dias.</p>
            ) : (
              upcomingBirthdays.map((birthday) => (
                <div key={birthday.id} className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  <p className="font-semibold text-rose-800">{birthday.fullName}</p>
                  <p className="text-xs text-rose-600">{formatDepartment(birthday.department, birthday.position)}</p>
                  <p className="mt-1 text-xs font-semibold text-rose-600">
                    {formatDateOnly(birthday.birthDate)} • {birthday.daysUntilBirthday === 0 ? "Hoje" : `${birthday.daysUntilBirthday} dia${birthday.daysUntilBirthday === 1 ? "" : "s"}`}
                  </p>
                </div>
              ))
            )}
          </div>
        </section>
      )}

      {overviewQuickTab === "withoutAccess" && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <header className="flex items-center gap-2 border-b border-slate-100 pb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
            <FaIdBadge className="text-slate-500" />
            Colaboradores sem acesso ao sistema
          </header>
          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {overviewLoading ? (
              <p className="text-sm text-slate-500">Carregando colaboradores...</p>
            ) : withoutSystemAccessList.length === 0 ? (
              <p className="text-sm text-slate-500">Todos os colaboradores cadastrados possuem acesso ao sistema.</p>
            ) : (
              withoutSystemAccessList.map((item) => (
                <div key={item.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                  <p className="font-semibold text-slate-800">{item.fullName}</p>
                  <p className="text-xs text-slate-500">{formatDepartment(item.department, item.position)}</p>
                  {item.hireDate && <p className="mt-1 text-xs text-slate-400">Admissão: {formatDateOnly(item.hireDate)}</p>}
                </div>
              ))
            )}
          </div>
        </section>
      )}

        </div>
      )}

      {overviewQuickTab === "benefits" && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <header className="flex flex-col gap-2 border-b border-slate-100 pb-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                <FaGift className="text-fuchsia-600" /> Benefícios
              </h2>
              <p className="mt-1 text-xs text-slate-400">Cadastre benefícios por tipo de vínculo para aplicar automaticamente aos colaboradores correspondentes.</p>
            </div>
          </header>
          {canManage && (
            <form onSubmit={handleBenefitCatalogSubmit} className="mt-4 grid gap-4 rounded-2xl border border-fuchsia-100 bg-fuchsia-50/40 p-4 md:grid-cols-[1fr_220px] xl:grid-cols-[1fr_220px_2fr_auto]">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-600">Nome do benefício</label>
                <input value={benefitForm.name} onChange={(event) => setBenefitForm((prev) => ({ ...prev, name: event.target.value }))} placeholder="Ex.: Vale alimentação" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-600">Valor</label>
                <input value={benefitForm.monthlyCost} onChange={(event) => setBenefitForm((prev) => ({ ...prev, monthlyCost: maskCurrency(event.target.value) }))} placeholder="R$ 0,00" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm" />
              </div>
              <fieldset className="rounded-xl border border-slate-200 bg-white p-3">
                <legend className="px-1 text-sm font-medium text-slate-600">Quem se aplica</legend>
                <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {EMPLOYMENT_TYPES.map((type) => (
                    <label key={type} className="inline-flex items-center gap-2 text-sm text-slate-600">
                      <input type="checkbox" checked={benefitForm.employmentTypes.includes(type)} onChange={() => toggleBenefitEmploymentType(type)} className="h-4 w-4 rounded border-slate-300 text-fuchsia-600 focus:ring-fuchsia-500" />
                      {type}
                    </label>
                  ))}
                </div>
              </fieldset>
              <div className="flex items-end gap-2">
                <button type="submit" className="inline-flex items-center justify-center gap-2 rounded-lg bg-fuchsia-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-fuchsia-700"><FaPlus /> {benefitEditingId ? "Salvar" : "Cadastrar"}</button>
                {benefitEditingId && <button type="button" onClick={resetBenefitForm} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600">Cancelar</button>}
              </div>
              {benefitError && <p className="text-sm font-semibold text-rose-600 xl:col-span-4">{benefitError}</p>}
            </form>
          )}
          <div className="mt-5 overflow-x-auto">
            {benefitCatalog.length === 0 ? (
              <p className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">Nenhum benefício cadastrado.</p>
            ) : (
              <table className="min-w-full divide-y divide-slate-200">
                <thead><tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500"><th className="px-4 py-3">Benefício</th><th className="px-4 py-3">Valor</th><th className="px-4 py-3">Tipos de vínculo</th>{canManage && <th className="px-4 py-3 text-right">Ações</th>}</tr></thead>
                <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
                  {benefitCatalog.map((benefit) => (
                    <tr key={benefit.id}><td className="px-4 py-3 font-semibold text-slate-900">{benefit.name}</td><td className="px-4 py-3">{formatCurrency(parseCurrencyInput(benefit.monthlyCost))}</td><td className="px-4 py-3"><div className="flex flex-wrap gap-1">{benefit.employmentTypes.map((type) => <span key={type} className="rounded-full bg-fuchsia-50 px-2 py-1 text-xs font-semibold text-fuchsia-700">{type}</span>)}</div></td>{canManage && <td className="px-4 py-3"><div className="flex justify-end gap-2"><button type="button" onClick={() => editBenefitCatalog(benefit)} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600"><FaEdit /> Editar</button><button type="button" onClick={() => removeBenefitCatalog(benefit.id)} className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2 py-1 text-xs font-medium text-rose-600"><FaTrash /> Excluir</button></div></td>}</tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}

      {(activeTab === "vacationControl" || (activeTab === "overview" && overviewQuickTab === "vacationControl")) && (
        <div className="space-y-8">{vacationControlSection}</div>
      )}

      {(activeTab === "employeeHistory" || (activeTab === "overview" && overviewQuickTab === "employeeHistory")) && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <header className="flex flex-col gap-3 border-b border-slate-100 pb-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Histórico do colaborador
              </h2>
              {focusedHistoryEmployee && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                  <span>Exibindo somente: <strong className="text-slate-700">{focusedHistoryEmployee.fullName}</strong></span>
                  <button type="button" onClick={() => setFocusedHistoryEmployeeId(null)} className="text-xs font-semibold text-blue-700 hover:text-blue-900">
                    Ver todos
                  </button>
                </div>
              )}
            </div>
            {canManage && (
              <button
                type="button"
                onClick={() => openHistoryModal()}
                className="inline-flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-semibold text-violet-700 shadow-sm transition hover:border-violet-300 hover:bg-violet-100"
              >
                <FaClipboardList /> Cadastrar histórico
              </button>
            )}
          </header>
          <div className="mt-4 overflow-x-auto">
            {historyLoading ? (
              <p className="text-sm text-slate-500">Carregando histórico...</p>
            ) : visibleEmployeeHistory.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhum histórico cadastrado até o momento.</p>
            ) : (
              <table className="min-w-full divide-y divide-slate-200">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3">Colaborador</th>
                    <th className="px-4 py-3">Tipo</th>
                    <th className="px-4 py-3">Período</th>
                    <th className="px-4 py-3">Dias</th>
                    <th className="px-4 py-3">Observações</th>
                    {canManage && <th className="px-4 py-3 text-right">Ações</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
                  {visibleEmployeeHistory.map((entry) => (
                    <tr key={entry.id}>
                      <td className="px-4 py-3 font-medium text-slate-900">{entry.employeeName}</td>
                      <td className="px-4 py-3">{HISTORY_TYPE_LABELS[entry.type] || entry.type}</td>
                      <td className="px-4 py-3">{formatDateRange(entry.startDate, entry.endDate)}</td>
                      <td className="px-4 py-3">{entry.days ? `${entry.days} dias` : "—"}</td>
                      <td className="px-4 py-3">{entry.notes || "—"}</td>
                      {canManage && (
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleEditHistory(entry)}
                              className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:border-slate-300"
                            >
                              <FaEdit /> Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteHistory(entry)}
                              className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2 py-1 text-xs font-medium text-rose-600 hover:border-rose-300"
                            >
                              <FaTrash /> Excluir
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}

      {activeTab === "ai" && (
        <AIInsightsPanel
          module="hr"
          title="Insights inteligentes de Recursos Humanos"
          subtitle="Análise executiva do quadro, férias, desligamentos, vínculos, custos, benefícios e indicadores disponíveis."
          data={{ employees, terminatedEmployees, overview, vacationControl, employeeHistory, benefitCatalog }}
        />
      )}

      {activeTab === "recruitmentSelection" && (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-2 rounded-2xl border border-blue-100 bg-gradient-to-r from-[#071f54] to-[#0d4d91] p-2 shadow-sm">
            {[{ id: "talentBank", label: "Banco de Talentos" }, { id: "vacancies", label: "Vagas" }].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setRecruitmentTab(tab.id)}
                className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${recruitmentTab === tab.id ? "bg-white text-blue-800 shadow-sm" : "text-blue-100 hover:bg-white/10 hover:text-white"}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {recruitmentTab === "talentBank" && (
            <div className="grid gap-6 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <header className="mb-4 border-b border-slate-100 pb-3">
              <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                <FaBriefcase className="text-blue-700" /> Banco de Talentos
              </h2>
            </header>
            <form onSubmit={handleTalentSubmit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-600">Nome do candidato</label>
                  <input name="candidateName" value={talentForm.candidateName} onChange={handleTalentFormChange} required className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-600">E-mail</label>
                  <input name="email" type="email" value={talentForm.email} onChange={handleTalentFormChange} className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-600">Telefone</label>
                  <input name="phone" value={talentForm.phone} onChange={handleTalentFormChange} className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-600">Vaga vinculada</label>
                  <select name="position" value={talentForm.position} onChange={handleTalentFormChange} className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50">
                    <option value="">Selecione uma vaga...</option>
                    {talentJobOptions.map((jobName) => (
                      <option key={jobName} value={jobName}>{jobName}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-slate-600">Currículo</label>
                  <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-blue-200 bg-blue-50 px-4 py-4 text-sm font-semibold text-blue-700 hover:bg-blue-100">
                    <FaUpload /> Selecionar anexo(s)
                    <input type="file" multiple accept={TALENT_ATTACHMENT_ACCEPT} onChange={handleTalentCurriculumChange} className="sr-only" />
                  </label>
                  {talentError && (
                    <p className="mt-2 text-xs font-semibold text-rose-600">{talentError}</p>
                  )}
                  {(talentCurriculumFile || []).length > 0 && (
                    <div className="mt-2 space-y-2">
                      {(talentCurriculumFile || []).map((file, index) => (
                        <div key={`${file.name}-${index}`} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
                          <span className="inline-flex items-center gap-2"><FaFilePdf /> {file.name}</span>
                          <button type="button" onClick={() => handleRemovePendingTalentFile(index)} className="font-semibold text-rose-600 hover:text-rose-700">Remover</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-slate-600">Observações</label>
                  <textarea name="notes" rows={3} value={talentForm.notes} onChange={handleTalentFormChange} className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="submit" disabled={talentSaving} className="inline-flex items-center gap-2 rounded-xl bg-[#0b3474] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0a2c62] disabled:cursor-not-allowed disabled:opacity-60">
                  <FaUpload /> {talentSaving ? "Salvando..." : talentEditingId ? "Atualizar cadastro" : "Salvar no banco de talentos"}
                </button>
                {talentEditingId && (
                  <button type="button" onClick={resetTalentForm} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm hover:border-slate-300">
                    Cancelar edição
                  </button>
                )}
              </div>
            </form>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <header className="mb-4 border-b border-slate-100 pb-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Currículos recebidos</h2>
            </header>
            <div className="mb-4 grid gap-3 lg:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Nome</label>
                <input name="name" value={talentFilters.name} onChange={handleTalentFilterChange} placeholder="Filtrar por nome" className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">E-mail</label>
                <input name="email" value={talentFilters.email} onChange={handleTalentFilterChange} placeholder="Filtrar por e-mail" className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Vaga</label>
                <input name="position" value={talentFilters.position} onChange={handleTalentFilterChange} placeholder="Filtrar por vaga" className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
              </div>
              <div className="lg:col-span-3">
                <button type="button" onClick={handleResetTalentFilters} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 shadow-sm hover:border-slate-300">
                  Limpar filtros
                </button>
              </div>
            </div>
            <div className="space-y-3">
              {talentLoading ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">
                  Carregando currículos...
                </div>
              ) : talentBankEntries.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">
                  Nenhum currículo anexado ainda.
                </div>
              ) : filteredTalentBankEntries.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">
                  Nenhum currículo encontrado com os filtros selecionados.
                </div>
              ) : (
                filteredTalentBankEntries.map((entry) => (
                  <article key={entry.id} className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50/70 p-4 shadow-sm transition hover:border-blue-200 hover:shadow-md">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-900">{entry.candidateName}</h3>
                        <p className="text-sm text-slate-500">{entry.position || "Sem vaga vinculada"}</p>
                        <p className="mt-1 text-xs text-slate-400">{entry.email || "Sem e-mail"} {entry.phone ? `• ${entry.phone}` : ""}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => setViewingTalentEntry(entry)} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-slate-300"><FaSearch /> Visualizar</button>
                        <button type="button" onClick={() => handleEditTalentEntry(entry)} className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:border-emerald-300"><FaEdit /> Alterar</button>
                        <button type="button" onClick={() => handleDeleteTalentEntry(entry.id)} className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:border-rose-300"><FaTrash /> Excluir</button>
                      </div>
                    </div>
                    <div className="mt-3 space-y-2">
                      {(entry.files || []).length === 0 ? (
                        <p className="text-xs text-slate-400">Nenhum arquivo anexado.</p>
                      ) : (
                        (entry.files || []).map((file) => (
                          <div key={file.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                            <span className="inline-flex items-center gap-2">{file.name?.toLowerCase().endsWith(".pdf") ? <FaFilePdf /> : <FaFileWord />} {file.name}</span>
                            <div className="flex gap-2">
                              <button type="button" onClick={() => handleDownloadTalentFile(file)} className="font-semibold text-blue-700 hover:text-blue-900"><FaDownload className="inline" /> Baixar</button>
                              <button type="button" onClick={() => handleDeleteTalentFile(entry.id, file.id)} className="font-semibold text-rose-600 hover:text-rose-700"><FaTrash className="inline" /> Excluir</button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    {entry.notes && <p className="mt-3 text-sm text-slate-600">{entry.notes}</p>}
                  </article>
                ))
              )}
            </div>
          </section>
            </div>
          )}

          {recruitmentTab === "vacancies" && (
            <div className="grid gap-6 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
              <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <header className="mb-4 border-b border-slate-100 pb-3">
                  <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                    <FaBriefcase className="text-blue-700" /> {talentJobEditingId ? "Editar vaga" : "Cadastrar vaga"}
                  </h2>
                </header>
                <form onSubmit={handleTalentJobSubmit} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-600">Nome da vaga</label>
                      <input name="name" value={talentJobForm.name} onChange={handleTalentJobFormChange} required className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-600">Data de publicação</label>
                      <input name="publicationDate" type="date" value={talentJobForm.publicationDate} onChange={handleTalentJobFormChange} className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-600">Status</label>
                      <select name="status" value={talentJobForm.status} onChange={handleTalentJobFormChange} className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50">
                        {TALENT_JOB_STATUS_OPTIONS.map((status) => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-600">Salário bruto</label>
                      <input name="salary" value={talentJobForm.salary} onChange={handleTalentJobFormChange} placeholder="Ex: R$ 3.000,00" className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-600">Carga horária</label>
                      <input name="workload" value={talentJobForm.workload} onChange={handleTalentJobFormChange} placeholder="Ex: 44h semanais" className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-sm font-medium text-slate-600">Descrição</label>
                      <textarea name="description" rows={3} value={talentJobForm.description} onChange={handleTalentJobFormChange} className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-sm font-medium text-slate-600">Requisitos</label>
                      <textarea name="requirements" rows={3} value={talentJobForm.requirements} onChange={handleTalentJobFormChange} className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
                    </div>
                    <div className="sm:col-span-2">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <label className="block text-sm font-medium text-slate-600">Benefícios</label>
                        <button type="button" onClick={addTalentJobBenefit} className="rounded-lg border border-blue-200 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50">+ Adicionar</button>
                      </div>
                      <div className="space-y-2">
                        {(talentJobForm.benefits || [""]).map((benefit, index) => (
                          <div key={index} className="flex gap-2">
                            <input value={benefit} onChange={(event) => handleTalentJobBenefitChange(index, event.target.value)} placeholder="Ex: Vale alimentação" className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
                            <button type="button" onClick={() => removeTalentJobBenefit(index)} className="rounded-lg border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50">Remover</button>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-sm font-medium text-slate-600">Observações</label>
                      <textarea name="notes" rows={3} value={talentJobForm.notes} onChange={handleTalentJobFormChange} className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
                    </div>
                  </div>
                  {talentJobError && <p className="text-sm font-semibold text-rose-600">{talentJobError}</p>}
                  <div className="flex flex-wrap gap-2">
                    <button type="submit" disabled={talentJobSaving} className="inline-flex items-center gap-2 rounded-xl bg-[#0b3474] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0a2c62] disabled:cursor-not-allowed disabled:opacity-60">
                      <FaUpload /> {talentJobSaving ? "Salvando..." : talentJobEditingId ? "Atualizar vaga" : "Salvar vaga"}
                    </button>
                    {talentJobEditingId && (
                      <button type="button" onClick={resetTalentJobForm} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm hover:border-slate-300">Cancelar edição</button>
                    )}
                  </div>
                </form>
              </section>

              <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <header className="mb-4 border-b border-slate-100 pb-3">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Vagas cadastradas</h2>
                </header>
                <div className="space-y-3">
                  {talentJobsLoading ? (
                    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">Carregando vagas...</div>
                  ) : talentJobs.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">Nenhuma vaga cadastrada ainda.</div>
                  ) : (
                    talentJobs.map((job) => (
                      <article key={job.id} className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50/70 p-4 shadow-sm transition hover:border-blue-200 hover:shadow-md">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <h3 className="font-semibold text-slate-900">{job.name}</h3>
                            <p className="text-sm text-slate-500">{job.publicationDate ? `Publicado em ${job.publicationDate}` : "Sem data de publicação"}</p>
                            <span className="mt-2 inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">{job.status || "Rascunho"}</span>
                            <p className="mt-1 text-xs text-slate-400">{job.salary || "Salário não informado"} {job.workload ? `• ${job.workload}` : ""}</p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button type="button" onClick={() => handleEditTalentJob(job)} className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:border-emerald-300"><FaEdit /> Alterar</button>
                            <button type="button" onClick={() => handleDeleteTalentJob(job.id)} className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:border-rose-300"><FaTrash /> Excluir</button>
                          </div>
                        </div>
                        {job.description && <p className="mt-3 text-sm text-slate-600">{job.description}</p>}
                        {job.requirements && <p className="mt-2 text-sm text-slate-600"><strong>Requisitos:</strong> {job.requirements}</p>}
                        {(job.benefits || []).length > 0 && <p className="mt-2 text-sm text-slate-600"><strong>Benefícios:</strong> {(job.benefits || []).join(", ")}</p>}
                        {job.notes && <p className="mt-2 text-sm text-slate-600"><strong>Observações:</strong> {job.notes}</p>}
                      </article>
                    ))
                  )}
                </div>
              </section>
            </div>
          )}
        </div>
      )}

      <Modal open={Boolean(viewingTalentEntry)} onClose={() => setViewingTalentEntry(null)} size="max-w-2xl" zIndex={70}>
        {viewingTalentEntry && (
          <div className="space-y-5">
            <header>
              <h2 className="text-xl font-semibold text-slate-800">{viewingTalentEntry.candidateName}</h2>
              <p className="text-sm text-slate-500">{viewingTalentEntry.position || "Sem vaga vinculada"}</p>
            </header>
            <div className="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 sm:grid-cols-2">
              <p><strong>E-mail:</strong> {viewingTalentEntry.email || "—"}</p>
              <p><strong>Telefone:</strong> {viewingTalentEntry.phone || "—"}</p>
              <p className="sm:col-span-2"><strong>Observações:</strong> {viewingTalentEntry.notes || "—"}</p>
            </div>
            <section>
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">Arquivos</h3>
              <div className="space-y-2">
                {(viewingTalentEntry.files || []).length === 0 ? (
                  <p className="rounded-lg border border-dashed border-slate-200 p-3 text-sm text-slate-500">Nenhum arquivo anexado.</p>
                ) : (
                  (viewingTalentEntry.files || []).map((file) => (
                    <div key={file.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600">
                      <span className="inline-flex items-center gap-2">{file.name?.toLowerCase().endsWith(".pdf") ? <FaFilePdf /> : <FaFileWord />} {file.name}</span>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => handleDownloadTalentFile(file)} className="inline-flex items-center gap-1 rounded-md border border-blue-200 px-2 py-1 text-xs font-semibold text-blue-700"><FaDownload /> Baixar</button>
                        <button type="button" onClick={() => handleDeleteTalentFile(viewingTalentEntry.id, file.id)} className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2 py-1 text-xs font-semibold text-rose-600"><FaTrash /> Excluir</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        )}
      </Modal>

      <Modal open={reportModalOpen} onClose={closeReportModal} size="max-w-2xl">
        <div className="space-y-5">
          <header className="space-y-1">
            <h2 className="text-xl font-semibold text-slate-800">Relatórios</h2>
            <p className="text-sm text-slate-500">
              Escolha entre Excel (.xlsx) e PDF, aplique filtros e selecione os campos do PDF.
            </p>
          </header>

          {reportError && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
              {reportError}
            </div>
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            <section className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4">
              <header className="mb-3">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
                  <FaFileExcel /> Excel
                </h3>
                <p className="mt-1 text-xs text-emerald-700">
                  Gere uma planilha .xlsx para edição e filtros no Excel.
                </p>
              </header>
              <div className="grid gap-2">
                <button
                  type="button"
                  onClick={() => downloadEmployeesExcel({}, { closeModal: true, onError: setReportError })}
                  disabled={downloadingExcel}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaFileExcel /> {downloadingExcel ? "Gerando Excel..." : "Excel completo"}
                </button>
                <button
                  type="button"
                  onClick={() => downloadEmployeesExcel(requestFilters, { closeModal: true, onError: setReportError })}
                  disabled={downloadingExcel}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 shadow-sm transition hover:border-emerald-300 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaSearch /> Excel com filtros atuais
                </button>
              </div>
            </section>

            <section className="rounded-2xl border border-sky-200 bg-sky-50/70 p-4">
              <header className="mb-3">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-sky-800">
                  <FaFilePdf /> PDF
                </h3>
                <p className="mt-1 text-xs text-sky-700">
                  Selecione os campos e gere um PDF pronto para leitura ou impressão.
                </p>
              </header>
              <div className="mb-3 grid gap-2 sm:grid-cols-2">
                {EMPLOYEE_REPORT_FIELDS.map((field) => (
                  <label key={field.key} className="flex items-center gap-2 rounded-lg border border-sky-100 bg-white px-3 py-2 text-xs font-medium text-slate-600">
                    <input
                      type="checkbox"
                      checked={selectedPdfFields.includes(field.key)}
                      onChange={() => handlePdfFieldToggle(field.key)}
                      className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-200"
                    />
                    {field.label}
                  </label>
                ))}
              </div>
              <div className="grid gap-2">
                <button
                  type="button"
                  onClick={() => downloadEmployeesPdf(buildPdfParams({}), { closeModal: true, onError: setReportError })}
                  disabled={downloadingPdf}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaFilePdf /> {downloadingPdf ? "Gerando PDF..." : "PDF completo"}
                </button>
                <button
                  type="button"
                  onClick={() => downloadEmployeesPdf(buildPdfParams(requestFilters), { closeModal: true, onError: setReportError })}
                  disabled={downloadingPdf}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-sky-200 bg-white px-4 py-2 text-sm font-semibold text-sky-700 shadow-sm transition hover:border-sky-300 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaSearch /> PDF com filtros atuais
                </button>
              </div>
            </section>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-600">Atalhos de Excel por status</h3>
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              <button type="button" onClick={() => handleReportDownload({ status: "CURRENT" })} disabled={downloadingExcel} className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 disabled:opacity-60"><FaUserTie /> Ativos, férias e licenças</button>
              <button type="button" onClick={() => handleReportDownload({ status: "ACTIVE" })} disabled={downloadingExcel} className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm font-semibold text-emerald-700 disabled:opacity-60"><FaUserTie /> Somente ativos</button>
              <button type="button" onClick={() => handleReportDownload({ status: "VACATION" })} disabled={downloadingExcel} className="inline-flex items-center gap-2 rounded-lg border border-sky-200 bg-white px-3 py-2 text-sm font-semibold text-sky-700 disabled:opacity-60"><FaUmbrellaBeach /> Férias</button>
              <button type="button" onClick={() => handleReportDownload({ status: "ON_LEAVE" })} disabled={downloadingExcel} className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm font-semibold text-amber-700 disabled:opacity-60"><FaUserClock /> Licença</button>
              <button type="button" onClick={() => handleReportDownload({ status: "TERMINATED" })} disabled={downloadingExcel} className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm font-semibold text-rose-700 disabled:opacity-60"><FaTrash /> Desligados</button>
              <button type="button" onClick={() => handleReportDownload(requestFilters)} disabled={downloadingExcel} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700 disabled:opacity-60"><FaSearch /> Filtros atuais</button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal open={historyModalOpen} onClose={closeHistoryModal} size="max-w-2xl" zIndex={80}>
        <form onSubmit={handleHistorySubmit} className="space-y-4">
          <header>
            <h2 className="text-xl font-semibold text-slate-800">
              {historyForm.id ? "Editar histórico" : "Cadastrar histórico"}
            </h2>
            <p className="text-sm text-slate-500">
              Registre licença, férias, ocorrência ou desligamento do colaborador.
            </p>
          </header>

          {historyError && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
              {historyError}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-600">Colaborador</label>
              <input
                type="text"
                value={historyEmployeeSearch}
                onChange={(event) => setHistoryEmployeeSearch(event.target.value)}
                placeholder="Filtrar colaborador..."
                className="mb-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              />
              <select
                value={historyForm.employeeId}
                onChange={(event) =>
                  setHistoryForm((prev) => ({ ...prev, employeeId: event.target.value }))
                }
                required
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              >
                <option value="">Selecione</option>
                {filteredActiveEmployeeOptions.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.fullName}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-400">
                Lista exibindo colaboradores ativos.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Tipo</label>
              <select
                value={historyForm.type}
                onChange={(event) =>
                  setHistoryForm((prev) => {
                    const nextType = event.target.value;
                    if (nextType !== "VACATION") {
                      return { ...prev, type: nextType };
                    }
                    return {
                      ...prev,
                      type: nextType,
                      endDate: calculateVacationEndDate(prev.startDate, prev.days),
                    };
                  })
                }
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              >
                {HISTORY_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Dias (férias)</label>
              <input
                type="number"
                min="1"
                value={historyForm.days}
                onChange={(event) => {
                  const nextDays = event.target.value;
                  setHistoryForm((prev) => ({
                    ...prev,
                    days: nextDays,
                    endDate:
                      prev.type === "VACATION"
                        ? calculateVacationEndDate(prev.startDate, nextDays)
                        : prev.endDate,
                  }));
                }}
                required={historyForm.type === "VACATION"}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Início</label>
              <input
                type="date"
                value={historyForm.startDate}
                onChange={(event) => {
                  const nextStartDate = event.target.value;
                  setHistoryForm((prev) => ({
                    ...prev,
                    startDate: nextStartDate,
                    endDate:
                      prev.type === "VACATION"
                        ? calculateVacationEndDate(nextStartDate, prev.days)
                        : prev.endDate,
                  }));
                }}
                required={historyForm.type === "VACATION"}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Fim</label>
              <input
                type="date"
                value={historyForm.endDate}
                onChange={(event) =>
                  setHistoryForm((prev) => ({ ...prev, endDate: event.target.value }))
                }
                readOnly={historyForm.type === "VACATION"}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-600">Observações</label>
              <textarea
                rows={3}
                value={historyForm.notes}
                onChange={(event) =>
                  setHistoryForm((prev) => ({ ...prev, notes: event.target.value }))
                }
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={closeHistoryModal}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={historySaving}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:bg-emerald-400"
            >
              {historySaving
                ? "Salvando..."
                : historyForm.id
                  ? "Salvar alterações"
                  : "Salvar histórico"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={importModalOpen} onClose={closeImportModal} size="max-w-2xl">
        <div className="space-y-5">
          <header className="rounded-2xl bg-gradient-to-r from-[#071f54] to-[#0d4d91] p-5 text-white">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15"><FaUmbrellaBeach /></span>
              <div>
                <h2 className="text-xl font-semibold">{importType === "VACATIONS" ? "Importar férias" : "Importação em Excel"}</h2>
                <p className="mt-1 text-sm text-blue-100">
                  {importType === "VACATIONS"
                    ? "Informe somente CPF, data de início e quantidade de dias. O restante é calculado pelo sistema."
                    : "Use o modelo do sistema para importar os dados com segurança."}
                </p>
              </div>
            </div>
          </header>

          {importError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm font-medium text-rose-700">
              {importError}
            </div>
          )}
          {importSummary && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm font-medium text-emerald-700">
              {importSummary}
            </div>
          )}

          {importType !== "VACATIONS" ? (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Tipo de importação</label>
              <select
                value={importType}
                onChange={(event) => setImportType(event.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 shadow-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
              >
                <option value="EMPLOYEES">Cadastro de colaboradores</option>
                <option value="HISTORY">Histórico geral</option>
              </select>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-3">
              {[
                ["1", "CPF", "Localiza automaticamente o colaborador."],
                ["2", "Data de início", "Define o primeiro dia das férias."],
                ["3", "Quantidade de dias", "Calcula a data final automaticamente."],
              ].map(([number, title, helper]) => (
                <div key={number} className="rounded-2xl border border-blue-100 bg-blue-50/50 p-3">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">{number}</span>
                  <p className="mt-2 text-sm font-semibold text-slate-800">{title}</p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">{helper}</p>
                </div>
              ))}
            </div>
          )}

          {importType !== "VACATIONS" && (
            <ol className="space-y-1 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
              <li><strong>1.</strong> Baixe e preencha o template.</li>
              <li><strong>2.</strong> Confira as instruções do arquivo.</li>
              <li><strong>3.</strong> Selecione a planilha para iniciar a importação.</li>
            </ol>
          )}

          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={handleDownloadImportTemplate}
              className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-semibold text-blue-700 shadow-sm transition hover:bg-blue-100"
            >
              <FaDownload /> {importType === "VACATIONS" ? "Baixar modelo de férias" : "Baixar template"}
            </button>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-[#0b3474] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0a2c62]">
              <FaFileImport /> {importingData ? "Importando..." : importType === "VACATIONS" ? "Selecionar planilha de férias" : "Selecionar planilha"}
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                disabled={importingData}
                onChange={handleImportFileChange}
              />
            </label>
          </div>

          {importType === "VACATIONS" && (
            <p className="text-xs text-slate-500">
              O CPF pode estar com ou sem pontuação. A data final das férias e a atualização do controle são feitas automaticamente após a importação.
            </p>
          )}
        </div>
      </Modal>

      <Modal open={vacationEditModalOpen} onClose={closeVacationEditModal} size="max-w-2xl">
        <form onSubmit={handleVacationControlSubmit} className="space-y-4">
          <header>
            <h2 className="text-xl font-semibold text-slate-800">Ajustar controle de férias</h2>
          </header>

          {vacationEditError && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
              {vacationEditError}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-600">Colaborador</label>
              <select
                value={vacationEditForm.employeeId}
                onChange={(event) =>
                  setVacationEditForm((prev) => ({ ...prev, employeeId: event.target.value }))
                }
                required
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              >
                <option value="">Selecione</option>
                {[...employees]
                  .sort((a, b) => (a.fullName || "").localeCompare(b.fullName || "", "pt-BR"))
                  .map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.fullName}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Saldo de férias (dias)</label>
              <input
                type="number"
                min="0"
                value={vacationEditForm.vacationDays}
                onChange={(event) =>
                  setVacationEditForm((prev) => ({ ...prev, vacationDays: event.target.value }))
                }
                required
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              />
            </div>
            <div />
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Período aquisitivo (início)</label>
              <input
                type="date"
                value={vacationEditForm.acquisitionPeriodStart}
                onChange={(event) => {
                  const startDate = event.target.value;
                  setVacationEditForm((prev) => ({
                    ...prev,
                    acquisitionPeriodStart: startDate,
                    acquisitionPeriodEnd: calculateAcquisitionEndDate(startDate),
                  }));
                }}
                required
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Período aquisitivo (fim)</label>
              <input
                type="date"
                value={vacationEditForm.acquisitionPeriodEnd}
                readOnly
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 shadow-sm"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={closeVacationEditModal}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={vacationEditSaving}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:bg-emerald-400"
            >
              {vacationEditSaving ? "Salvando..." : "Salvar controle"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={modalOpen} onClose={closeModal} size="max-w-6xl">
        <div className="space-y-6">
          <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1">
              <h2 className="text-xl font-semibold text-slate-800">
                {editingId ? "Editar colaborador" : "Novo colaborador"}
              </h2>
            </div>
            {editingId && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={openEmployeeHistoryTab}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300"
                >
                  <FaClipboardList /> Histórico
                </button>
                {canManage && (
                  <button
                    type="button"
                    onClick={() => openHistoryModal(currentEditingEmployee)}
                    className="inline-flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-sm font-semibold text-violet-700 shadow-sm transition hover:border-violet-300 hover:bg-violet-100"
                  >
                    <FaClipboardList /> Cadastrar histórico
                  </button>
                )}
              </div>
            )}
          </header>

          {formError && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
              {formError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <section className={FORM_SECTION_CLASS}>
              <div className={FORM_SECTION_HEADER_CLASS}>
                <span className={FORM_SECTION_BADGE_CLASS}>1</span>
                <div>
                  <h3 className={FORM_SECTION_TITLE_CLASS}>Dados pessoais</h3>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <div className="rounded-xl border border-blue-100 bg-blue-50/70 p-3 md:col-span-2 xl:col-span-3">
                  <label htmlFor="hr-form-userId" className="mb-1 block text-sm font-medium text-slate-600">
                    Usuário associado
                  </label>
                  <select
                    id="hr-form-userId"
                    name="userId"
                    value={form.userId}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  >
                    <option value="">Sem acesso ao sistema</option>
                    {availableUsers.map((systemUser) => (
                      <option
                        key={systemUser.id}
                        value={String(systemUser.id)}
                        disabled={
                          systemUser.employeeId &&
                          systemUser.employeeId !== editingId
                        }
                      >
                        {systemUser.name} — {systemUser.email}
                        {systemUser.sectorName ? ` • ${systemUser.sectorName}` : ""}
                        {systemUser.employeeId &&
                        systemUser.employeeId !== editingId
                          ? " (já vinculado)"
                          : ""}
                      </option>
                    ))}
                  </select>
                  {usersLoading && (
                    <p className="mt-1 text-xs text-slate-400">Carregando usuários...</p>
                  )}
                </div>
                <div className="grid gap-4 md:col-span-2 md:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)] xl:col-span-3">
                  <div>
                    <label htmlFor="hr-form-fullName" className="mb-1 block text-sm font-medium text-slate-600">
                      Nome completo
                    </label>
                    <input
                      id="hr-form-fullName"
                      name="fullName"
                      value={form.fullName}
                      onChange={handleFormChange}
                      required
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                    />
                  </div>
                  <div className="rounded-xl border border-dashed border-slate-300 bg-white p-3">
                    <label htmlFor="hr-form-photoUrl" className="mb-2 block text-sm font-medium text-slate-600">
                      Foto do colaborador
                    </label>
                    <div className="flex items-center gap-3">
                      <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-xs font-semibold text-slate-400">
                        {form.photoUrl ? (
                          <img src={form.photoUrl} alt="Prévia do colaborador" className="h-full w-full object-cover" />
                        ) : (
                          "Sem foto"
                        )}
                      </div>
                      <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 shadow-sm hover:border-emerald-300 hover:bg-emerald-100">
                        <FaUpload />
                        {form.photoUrl ? "Alterar foto" : "Enviar foto"}
                        <input
                          id="hr-form-photoUrl"
                          name="photoUrl"
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="sr-only"
                        />
                      </label>
                    </div>
                  </div>
                </div>
                <div>
                  <label htmlFor="hr-form-socialName" className="mb-1 block text-sm font-medium text-slate-600">
                    Nome social
                  </label>
                  <input
                    id="hr-form-socialName"
                    name="socialName"
                    value={form.socialName}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-email" className="mb-1 block text-sm font-medium text-slate-600">
                    E-mail corporativo
                  </label>
                  <div className="relative">
                    <FaEnvelope className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      id="hr-form-email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleFormChange}
                      className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="hr-form-phone" className="mb-1 block text-sm font-medium text-slate-600">
                    Telefone
                  </label>
                  <div className="relative">
                    <FaPhone className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      id="hr-form-phone"
                      name="phone"
                      value={form.phone}
                      onChange={handleFormChange}
                      className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="hr-form-cpf" className="mb-1 block text-sm font-medium text-slate-600">
                    CPF
                  </label>
                  <input
                    id="hr-form-cpf"
                    name="cpf"
                    value={form.cpf}
                    onChange={handleFormChange}
                    maxLength={14}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-rg" className="mb-1 block text-sm font-medium text-slate-600">
                    RG
                  </label>
                  <input
                    id="hr-form-rg"
                    name="rg"
                    value={form.rg}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-birthDate" className="mb-1 block text-sm font-medium text-slate-600">
                    Data de nascimento
                  </label>
                  <input
                    id="hr-form-birthDate"
                    name="birthDate"
                    type="date"
                    value={form.birthDate}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-gender" className="mb-1 block text-sm font-medium text-slate-600">
                    Gênero
                  </label>
                  <select
                    id="hr-form-gender"
                    name="gender"
                    value={form.gender}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  >
                    <option value="">Selecione</option>
                    <option value="Feminino">Feminino</option>
                    <option value="Masculino">Masculino</option>
                    <option value="Não binário">Não binário</option>
                    <option value="Outro">Outro</option>
                    <option value="Prefiro não informar">Prefiro não informar</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="hr-form-maritalStatus" className="mb-1 block text-sm font-medium text-slate-600">
                    Estado civil
                  </label>
                  <select
                    id="hr-form-maritalStatus"
                    name="maritalStatus"
                    value={form.maritalStatus}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  >
                    <option value="">Selecione</option>
                    <option value="Solteiro(a)">Solteiro(a)</option>
                    <option value="Casado(a)">Casado(a)</option>
                    <option value="União estável">União estável</option>
                    <option value="Divorciado(a)">Divorciado(a)</option>
                    <option value="Viúvo(a)">Viúvo(a)</option>
                  </select>
                </div>
              </div>
            </section>

            <section className={FORM_SECTION_CLASS}>
              <div className={FORM_SECTION_HEADER_CLASS}>
                <span className={FORM_SECTION_BADGE_CLASS}>2</span>
                <div>
                  <h3 className={FORM_SECTION_TITLE_CLASS}>Dados corporativos</h3>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <div>
                  <label htmlFor="hr-form-registrationNumber" className="mb-1 block text-sm font-medium text-slate-600">
                    Matrícula
                  </label>
                  <div className="relative">
                    <FaIdBadge className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      id="hr-form-registrationNumber"
                      name="registrationNumber"
                      value={form.registrationNumber}
                      onChange={handleFormChange}
                      className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="hr-form-department" className="mb-1 block text-sm font-medium text-slate-600">
                    Departamento
                  </label>
                  <div className="relative">
                    <FaBuilding className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      id="hr-form-department"
                      name="department"
                      value={form.department}
                      onChange={handleFormChange}
                      className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="hr-form-position" className="mb-1 block text-sm font-medium text-slate-600">
                    Cargo
                  </label>
                  <input
                    id="hr-form-position"
                    name="position"
                    value={form.position}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-employmentType" className="mb-1 block text-sm font-medium text-slate-600">
                    Tipo de vínculo
                  </label>
                  <select
                    id="hr-form-employmentType"
                    name="employmentType"
                    value={form.employmentType}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  >
                    <option value="">Selecione</option>
                    {EMPLOYMENT_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="hr-form-hireDate" className="mb-1 block text-sm font-medium text-slate-600">
                    Data de admissão
                  </label>
                  <input
                    id="hr-form-hireDate"
                    name="hireDate"
                    type="date"
                    value={form.hireDate}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-terminationDate" className="mb-1 block text-sm font-medium text-slate-600">
                    Fim do contrato
                  </label>
                  <input
                    id="hr-form-terminationDate"
                    name="terminationDate"
                    type="date"
                    value={form.terminationDate}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-workShift" className="mb-1 block text-sm font-medium text-slate-600">
                    Jornada / turno
                  </label>
                  <input
                    id="hr-form-workShift"
                    name="workShift"
                    value={form.workShift}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-vacationDays" className="mb-1 block text-sm font-medium text-slate-600">
                    Saldo de férias (dias)
                  </label>
                  <input
                    id="hr-form-vacationDays"
                    name="vacationDays"
                    type="number"
                    value={form.vacationDays}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-status" className="mb-1 block text-sm font-medium text-slate-600">
                    Status
                  </label>
                  <select
                    id="hr-form-status"
                    name="status"
                    value={form.status}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  >
                    {STATUS_OPTIONS.filter((option) => option.value !== "ALL").map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

            <section className={FORM_SECTION_CLASS}>
              <div className={FORM_SECTION_HEADER_CLASS}>
                <span className={FORM_SECTION_BADGE_CLASS}>2.1</span>
                <div>
                  <h3 className={FORM_SECTION_TITLE_CLASS}>Custo financeiro</h3>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <label htmlFor="hr-form-salary" className="mb-1 block text-sm font-medium text-slate-600">{isCltCostEmploymentType(form.employmentType) ? "Salário bruto" : "Salário"}</label>
                  <input id="hr-form-salary" name="salary" value={form.salary} onChange={handleFormChange} placeholder="R$ 0,00" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100" />
                </div>
                {isCltCostEmploymentType(form.employmentType) && (
                  <div>
                    <label htmlFor="hr-form-fgtsPercentage" className="mb-1 block text-sm font-medium text-slate-600">Porcentagem de FGTS (%)</label>
                    <input id="hr-form-fgtsPercentage" name="fgtsPercentage" type="number" min="0" step="0.01" value={form.fgtsPercentage} onChange={handleFormChange} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100" />
                  </div>
                )}
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 xl:col-span-2">
                  <p className="font-semibold">Custo mensal total</p>
                  <p className="mt-1 text-xl font-bold">{formatCurrency(financialCost.total)}</p>
                  <div className="mt-2 grid gap-1 text-xs text-emerald-700 sm:grid-cols-2">
                    <span>Salário: {formatCurrency(financialCost.salary)}</span>
                    <span>Benefícios: {formatCurrency(financialCost.benefitsTotal)}</span>
                    {isCltCostEmploymentType(form.employmentType) && (
                      <>
                        <span>FGTS: {formatCurrency(financialCost.fgtsCost)}</span>
                        <span>Férias 1/12: {formatCurrency(financialCost.vacationProvision)}</span>
                        <span>1/3 férias: {formatCurrency(financialCost.vacationThirdProvision)}</span>
                        <span>13º 1/12: {formatCurrency(financialCost.thirteenthProvision)}</span>
                        <span>INSS patronal: {formatCurrency(financialCost.employerInssCost)}</span>
                        <span>PIS: {formatCurrency(financialCost.pisCost)}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-600">Benefícios</span>
                  <button type="button" onClick={addBenefit} className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700"><FaPlus /> Incluir benefício</button>
                </div>
                {(form.benefits || []).map((benefit, index) => (
                  <div key={index} className="grid gap-3 rounded-xl border border-slate-200 bg-white p-3 md:grid-cols-[1fr_220px_auto]">
                    <div className="space-y-1">
                      {benefit.catalogBenefitId && <span className="inline-flex w-max rounded-full bg-fuchsia-50 px-2 py-0.5 text-[11px] font-semibold text-fuchsia-700">Automático por vínculo</span>}
                      <input aria-label="Benefício" value={benefit.name} onChange={(event) => handleBenefitChange(index, "name", event.target.value)} placeholder="Ex.: Vale alimentação" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                    </div>
                    <input aria-label="Custo mensal do benefício" value={benefit.monthlyCost} onChange={(event) => handleBenefitChange(index, "monthlyCost", event.target.value)} placeholder="Custo mensal" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                    <button type="button" onClick={() => removeBenefit(index)} className="rounded-lg border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-600">Remover</button>
                  </div>
                ))}
              </div>
            </section>

            <section className={FORM_SECTION_CLASS}>
              <div className={FORM_SECTION_HEADER_CLASS}>
                <span className={FORM_SECTION_BADGE_CLASS}>3</span>
                <div>
                  <h3 className={FORM_SECTION_TITLE_CLASS}>Endereço</h3>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <div>
                  <label htmlFor="hr-form-addressZipCode" className="mb-1 block text-sm font-medium text-slate-600">
                    CEP
                  </label>
                  <input
                    id="hr-form-addressZipCode"
                    name="addressZipCode"
                    value={form.addressZipCode}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div className="xl:col-span-2">
                  <label htmlFor="hr-form-addressStreet" className="mb-1 block text-sm font-medium text-slate-600">
                    Rua / logradouro
                  </label>
                  <input
                    id="hr-form-addressStreet"
                    name="addressStreet"
                    value={form.addressStreet}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-addressNumber" className="mb-1 block text-sm font-medium text-slate-600">
                    Número
                  </label>
                  <input
                    id="hr-form-addressNumber"
                    name="addressNumber"
                    value={form.addressNumber}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-addressComplement" className="mb-1 block text-sm font-medium text-slate-600">
                    Complemento
                  </label>
                  <input
                    id="hr-form-addressComplement"
                    name="addressComplement"
                    value={form.addressComplement}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-addressNeighborhood" className="mb-1 block text-sm font-medium text-slate-600">
                    Bairro
                  </label>
                  <input
                    id="hr-form-addressNeighborhood"
                    name="addressNeighborhood"
                    value={form.addressNeighborhood}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-addressCity" className="mb-1 block text-sm font-medium text-slate-600">
                    Cidade
                  </label>
                  <input
                    id="hr-form-addressCity"
                    name="addressCity"
                    value={form.addressCity}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-addressState" className="mb-1 block text-sm font-medium text-slate-600">
                    UF
                  </label>
                  <input
                    id="hr-form-addressState"
                    name="addressState"
                    value={form.addressState}
                    onChange={handleFormChange}
                    maxLength={2}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm uppercase text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
              </div>
            </section>

            <section className={FORM_SECTION_CLASS}>
              <div className={FORM_SECTION_HEADER_CLASS}>
                <span className={FORM_SECTION_BADGE_CLASS}>4</span>
                <div>
                  <h3 className={FORM_SECTION_TITLE_CLASS}>Contato de emergência</h3>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="hr-form-emergencyContactName" className="mb-1 block text-sm font-medium text-slate-600">
                    Contato de emergência
                  </label>
                  <input
                    id="hr-form-emergencyContactName"
                    name="emergencyContactName"
                    value={form.emergencyContactName}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-emergencyContactPhone" className="mb-1 block text-sm font-medium text-slate-600">
                    Telefone de emergência
                  </label>
                  <input
                    id="hr-form-emergencyContactPhone"
                    name="emergencyContactPhone"
                    value={form.emergencyContactPhone}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
              </div>
            </section>

            <section className={FORM_SECTION_CLASS}>
              <div className={FORM_SECTION_HEADER_CLASS}>
                <span className={FORM_SECTION_BADGE_CLASS}>5</span>
                <div>
                  <h3 className={FORM_SECTION_TITLE_CLASS}>Arquivos do colaborador</h3>
                </div>
              </div>
              <div className="space-y-3">
                {(form.employeeFiles || []).map((file, index) => (
                  <div key={index} className="grid gap-3 rounded-xl border border-slate-200 bg-white p-3 md:grid-cols-[1fr_260px_auto]">
                    <label className="cursor-pointer rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-600">
                      {file.name || "Selecionar documento"}
                      <input type="file" className="sr-only" onChange={(event) => handleEmployeeDocumentUpload(index, event.target.files?.[0])} />
                    </label>
                    <select value={file.category} onChange={(event) => handleEmployeeFileChange(index, "category", event.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700">
                      {EMPLOYEE_DOCUMENT_CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}
                    </select>
                    <div className="flex flex-wrap gap-2">
                      {file.dataUrl && <button type="button" onClick={() => openEmployeeFile(file, false)} className="inline-flex items-center gap-1 rounded-lg border border-blue-200 px-3 py-2 text-xs font-semibold text-blue-700"><FaExternalLinkAlt /> Visualizar</button>}
                      {file.dataUrl && <button type="button" onClick={() => openEmployeeFile(file, true)} className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 px-3 py-2 text-xs font-semibold text-emerald-700"><FaDownload /> Baixar</button>}
                      <button type="button" onClick={() => removeEmployeeFile(index)} className="rounded-lg border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-600">Remover</button>
                    </div>
                  </div>
                ))}
                <button type="button" onClick={addEmployeeFile} className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700"><FaPlus /> Incluir documento</button>
              </div>
            </section>

            {canManage && (
              <section className={FORM_SECTION_CLASS}>
                <div className={FORM_SECTION_HEADER_CLASS}>
                  <span className={FORM_SECTION_BADGE_CLASS}>6</span>
                  <div>
                    <h3 className={FORM_SECTION_TITLE_CLASS}>Acesso ao sistema</h3>
                  </div>
                </div>
                <div>
                  <label htmlFor="hr-form-notes" className="mb-1 block text-sm font-medium text-slate-600">
                    Observações internas
                  </label>
                  <textarea
                    id="hr-form-notes"
                    name="notes"
                    rows={4}
                    value={form.notes}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                    <span className="text-sm font-medium text-slate-600">
                      Responsáveis durante férias
                    </span>
                    <span className="text-xs text-slate-400">
                      Disponível quando o status do colaborador for "Férias".
                    </span>
                  </div>
                  {form.status === "VACATION" ? (
                    delegateOptions.length ? (
                      <div className="mt-2 max-h-48 space-y-2 overflow-y-auto rounded-lg border border-slate-200 bg-white p-3">
                        {delegateOptions.map((option) => {
                          const optionId = String(option.id);
                          const checked = form.vacationDelegateUserIds.includes(optionId);
                          return (
                            <label
                              key={option.id}
                              className="flex items-start gap-2 rounded-md border border-transparent px-2 py-1.5 text-sm text-slate-700 transition hover:border-emerald-200 hover:bg-emerald-50"
                            >
                              <input
                                type="checkbox"
                                className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                checked={checked}
                                onChange={() => handleDelegateToggle(option.id)}
                              />
                              <span>
                                <span className="font-medium">{option.name}</span>
                                <span className="block text-xs text-slate-500">{option.email}</span>
                                {option.sectorName && (
                                  <span className="block text-xs text-slate-400">{option.sectorName}</span>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="mt-2 text-xs text-slate-400">
                        Nenhum usuário disponível para atuar como substituto.
                      </p>
                    )
                  ) : (
                    <p className="mt-2 text-xs text-slate-400">
                      Defina o status como "Férias" para escolher responsáveis substitutos.
                    </p>
                  )}
                </div>
              </section>
            )}

            {!canManage && (
              <section>
                <label htmlFor="hr-form-notes" className="mb-1 block text-sm font-medium text-slate-600">
                  Observações internas
                </label>
                <textarea
                  id="hr-form-notes"
                  name="notes"
                  rows={4}
                  value={form.notes}
                  onChange={handleFormChange}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                />
              </section>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
              >
                Cancelar
              </button>
              {canManage && (
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400"
                >
                  {saving ? "Salvando..." : "Salvar colaborador"}
                </button>
              )}
            </div>
          </form>
        </div>
      </Modal>
      <FilePreviewModal
        isOpen={Boolean(previewEmployeeFile)}
        fileName={previewEmployeeFile?.name || "documento"}
        loadFile={loadEmployeePreview}
        onClose={() => setPreviewEmployeeFile(null)}
        onDownload={() => openEmployeeFile(previewEmployeeFile, true)}
      />
    </div>
  );
}
